import { useApp } from '@/store';
export { Lang, LANGUAGES } from '@/lib/lang';
import { Lang } from '@/lib/lang';

// L() expands one English string into all 8 supported locales as a placeholder.
// Used for new keys that haven't been translated yet — replace with real
// translations as they arrive.
const L = (en: string) => ({
  en,
  de: en,
  fr: en,
  it: en,
  es: en,
  pt: en,
  nl: en,
  pl: en,
});

const dict = {
  // Nav
  'nav.main': {
    en: 'Main',
    de: 'Hauptmenü',
    fr: 'Principal',
    it: 'Principale',
    es: 'Principal',
    pt: 'Principal',
    nl: 'Hoofdmenu',
    pl: 'Główne',
  },
  'nav.admin': {
    en: 'Administration',
    de: 'Verwaltung',
    fr: 'Administration',
    it: 'Amministrazione',
    es: 'Administración',
    pt: 'Administração',
    nl: 'Beheer',
    pl: 'Administracja',
  },
  'nav.data': {
    en: 'Data',
    de: 'Daten',
    fr: 'Données',
    it: 'Dati',
    es: 'Datos',
    pt: 'Dados',
    nl: 'Gegevens',
    pl: 'Dane',
  },
  'nav.overview': {
    en: 'Overview',
    de: 'Übersicht',
    fr: 'Aperçu',
    it: 'Panoramica',
    es: 'Resumen',
    pt: 'Visão geral',
    nl: 'Overzicht',
    pl: 'Przegląd',
  },
  'nav.map': {
    en: 'Map',
    de: 'Karte',
    fr: 'Carte',
    it: 'Mappa',
    es: 'Mapa',
    pt: 'Mapa',
    nl: 'Kaart',
    pl: 'Mapa',
  },
  'nav.reports': {
    en: 'Reports',
    de: 'Rapporte',
    fr: 'Rapports',
    it: 'Rapporti',
    es: 'Informes',
    pt: 'Relatórios',
    nl: 'Rapporten',
    pl: 'Raporty',
  },
  'nav.manufacturers': {
    en: 'Manufacturers',
    de: 'Hersteller',
    fr: 'Fabricants',
    it: 'Produttori',
    es: 'Fabricantes',
    pt: 'Fabricantes',
    nl: 'Fabrikanten',
    pl: 'Producenci',
  },
  'nav.deviceTypes': {
    en: 'Device Types',
    de: 'Gerätetypen',
    fr: "Types d'appareils",
    it: 'Tipi di dispositivi',
    es: 'Tipos de dispositivos',
    pt: 'Tipos de dispositivos',
    nl: 'Apparaattypen',
    pl: 'Typy urządzeń',
  },
  'nav.spareParts': {
    en: 'Spare Parts',
    de: 'Ersatzteile',
    fr: 'Pièces de rechange',
    it: 'Ricambi',
    es: 'Repuestos',
    pt: 'Peças sobressalentes',
    nl: 'Onderdelen',
    pl: 'Części zamienne',
  },
  'nav.servicePrices': {
    en: 'Service Prices',
    de: 'Servicepreise',
    fr: 'Tarifs',
    it: 'Prezzi servizio',
    es: 'Precios de servicio',
    pt: 'Preços de serviço',
    nl: 'Serviceprijzen',
    pl: 'Ceny usług',
  },
  'nav.workers': {
    en: 'Workers',
    de: 'Mitarbeiter',
    fr: 'Techniciens',
    it: 'Tecnici',
    es: 'Técnicos',
    pt: 'Técnicos',
    nl: 'Medewerkers',
    pl: 'Pracownicy',
  },
  'nav.settings': {
    en: 'Settings',
    de: 'Einstellungen',
    fr: 'Paramètres',
    it: 'Impostazioni',
    es: 'Configuración',
    pt: 'Configurações',
    nl: 'Instellingen',
    pl: 'Ustawienia',
  },
  'nav.hoseOptions': {
    en: 'Hose Options',
    de: 'Schlauchoptionen',
    fr: 'Options tuyaux',
    it: 'Opzioni manichetta',
    es: 'Opciones manguera',
    pt: 'Opções mangueira',
    nl: 'Slangopties',
    pl: 'Opcje węży',
  },
  'nav.importExport': {
    en: 'Import / Export',
    de: 'Import / Export',
    fr: 'Import / Export',
    it: 'Import / Export',
    es: 'Importar / Exportar',
    pt: 'Importar / Exportar',
    nl: 'Import / Export',
    pl: 'Import / Eksport',
  },

  // Header
  'header.online': {
    en: 'Online',
    de: 'Online',
    fr: 'En ligne',
    it: 'Online',
    es: 'En línea',
    pt: 'Online',
    nl: 'Online',
    pl: 'Online',
  },
  'header.offline': {
    en: 'Offline',
    de: 'Offline',
    fr: 'Hors ligne',
    it: 'Offline',
    es: 'Sin conexión',
    pt: 'Offline',
    nl: 'Offline',
    pl: 'Offline',
  },
  'sync.saveFailed': {
    en: 'Save failed — will retry',
    de: 'Speichern fehlgeschlagen — wird wiederholt',
    fr: 'Échec de l’enregistrement — nouvel essai',
    it: 'Salvataggio non riuscito — verrà ritentato',
    es: 'Error al guardar — se reintentará',
    pt: 'Falha ao guardar — será repetido',
    nl: 'Opslaan mislukt — opnieuw proberen',
    pl: 'Zapis nie powiódł się — ponowna próba',
  },
  'sync.rejected': {
    en: 'Change rejected by the server',
    de: 'Änderung vom Server abgelehnt',
    fr: 'Modification rejetée par le serveur',
    it: 'Modifica rifiutata dal server',
    es: 'Cambio rechazado por el servidor',
    pt: 'Alteração rejeitada pelo servidor',
    nl: 'Wijziging geweigerd door de server',
    pl: 'Zmiana odrzucona przez serwer',
  },
  'perm.denied': {
    en: 'You do not have permission for this change',
    de: 'Sie haben keine Berechtigung für diese Änderung',
    fr: 'Vous n’avez pas l’autorisation pour cette modification',
    it: 'Non hai i permessi per questa modifica',
    es: 'No tienes permiso para este cambio',
    pt: 'Não tem permissão para esta alteração',
    nl: 'U heeft geen toestemming voor deze wijziging',
    pl: 'Nie masz uprawnień do tej zmiany',
  },
  'header.help': {
    en: 'Help',
    de: 'Hilfe',
    fr: 'Aide',
    it: 'Aiuto',
    es: 'Ayuda',
    pt: 'Ajuda',
    nl: 'Help',
    pl: 'Pomoc',
  },
  'header.tour': {
    en: 'Tour',
    de: 'Tour',
    fr: 'Visite',
    it: 'Tour',
    es: 'Tour',
    pt: 'Tour',
    nl: 'Rondleiding',
    pl: 'Przewodnik',
  },
  'header.lang': {
    en: 'Language',
    de: 'Sprache',
    fr: 'Langue',
    it: 'Lingua',
    es: 'Idioma',
    pt: 'Idioma',
    nl: 'Taal',
    pl: 'Język',
  },

  // Common
  'common.back': {
    en: 'Back',
    de: 'Zurück',
    fr: 'Retour',
    it: 'Indietro',
    es: 'Atrás',
    pt: 'Voltar',
    nl: 'Terug',
    pl: 'Wstecz',
  },
  'common.backToOverview': {
    en: 'Back to overview',
    de: 'Zurück zur Übersicht',
    fr: "Retour à l'aperçu",
    it: 'Torna alla panoramica',
    es: 'Volver al resumen',
    pt: 'Voltar à visão geral',
    nl: 'Terug naar overzicht',
    pl: 'Powrót do przeglądu',
  },
  'common.save': {
    en: 'Save',
    de: 'Speichern',
    fr: 'Enregistrer',
    it: 'Salva',
    es: 'Guardar',
    pt: 'Salvar',
    nl: 'Opslaan',
    pl: 'Zapisz',
  },
  'common.cancel': {
    en: 'Cancel',
    de: 'Abbrechen',
    fr: 'Annuler',
    it: 'Annulla',
    es: 'Cancelar',
    pt: 'Cancelar',
    nl: 'Annuleren',
    pl: 'Anuluj',
  },
  'common.delete': {
    en: 'Delete',
    de: 'Löschen',
    fr: 'Supprimer',
    it: 'Elimina',
    es: 'Eliminar',
    pt: 'Excluir',
    nl: 'Verwijderen',
    pl: 'Usuń',
  },
  'common.edit': {
    en: 'Edit',
    de: 'Bearbeiten',
    fr: 'Modifier',
    it: 'Modifica',
    es: 'Editar',
    pt: 'Editar',
    nl: 'Bewerken',
    pl: 'Edytuj',
  },
  'common.add': {
    en: 'Add',
    de: 'Hinzufügen',
    fr: 'Ajouter',
    it: 'Aggiungi',
    es: 'Añadir',
    pt: 'Adicionar',
    nl: 'Toevoegen',
    pl: 'Dodaj',
  },
  'common.new': {
    en: 'New',
    de: 'Neu',
    fr: 'Nouveau',
    it: 'Nuovo',
    es: 'Nuevo',
    pt: 'Novo',
    nl: 'Nieuw',
    pl: 'Nowy',
  },
  'common.print': {
    en: 'Print',
    de: 'Drucken',
    fr: 'Imprimer',
    it: 'Stampa',
    es: 'Imprimir',
    pt: 'Imprimir',
    nl: 'Afdrukken',
    pl: 'Drukuj',
  },
  'common.pdf': {
    en: 'PDF',
    de: 'PDF',
    fr: 'PDF',
    it: 'PDF',
    es: 'PDF',
    pt: 'PDF',
    nl: 'PDF',
    pl: 'PDF',
  },
  'common.email': {
    en: 'Email',
    de: 'E-Mail',
    fr: 'E-mail',
    it: 'Email',
    es: 'Correo',
    pt: 'E-mail',
    nl: 'E-mail',
    pl: 'E-mail',
  },
  'common.today': {
    en: 'Today',
    de: 'Heute',
    fr: "Aujourd'hui",
    it: 'Oggi',
    es: 'Hoy',
    pt: 'Hoje',
    nl: 'Vandaag',
    pl: 'Dziś',
  },
  'common.notFound': {
    en: 'Customer not found',
    de: 'Kunde nicht gefunden',
    fr: 'Client introuvable',
    it: 'Cliente non trovato',
    es: 'Cliente no encontrado',
    pt: 'Cliente não encontrado',
    nl: 'Klant niet gevonden',
    pl: 'Klient nie znaleziony',
  },
  'common.invalidYear': {
    en: 'Invalid year',
    de: 'Ungültiges Jahr',
    fr: 'Année invalide',
    it: 'Anno non valido',
    es: 'Año inválido',
    pt: 'Ano inválido',
    nl: 'Ongeldig jaar',
    pl: 'Nieprawidłowy rok',
  },
  'reports.noWarnings': {
    en: 'No warnings',
    de: 'Keine Warnungen',
    fr: 'Aucun avertissement',
    it: 'Nessun avviso',
    es: 'Sin advertencias',
    pt: 'Sem avisos',
    nl: 'Geen waarschuwingen',
    pl: 'Brak ostrzeżeń',
  },
  'reports.allCurrent': {
    en: 'All current',
    de: 'Alle aktuell',
    fr: 'Tout à jour',
    it: 'Tutto attuale',
    es: 'Todo actual',
    pt: 'Tudo atual',
    nl: 'Alles actueel',
    pl: 'Wszystko aktualne',
  },
  'reports.generate': {
    en: 'Generate report',
    de: 'Bericht erstellen',
    fr: 'Générer un rapport',
    it: 'Genera rapporto',
    es: 'Generar informe',
    pt: 'Gerar relatório',
    nl: 'Rapport genereren',
    pl: 'Generuj raport',
  },
  'reports.reportType': {
    en: 'Report type',
    de: 'Berichtstyp',
    fr: 'Type de rapport',
    it: 'Tipo di rapporto',
    es: 'Tipo de informe',
    pt: 'Tipo de relatório',
    nl: 'Rapporttype',
    pl: 'Typ raportu',
  },
  'reports.format': {
    en: 'Format',
    de: 'Format',
    fr: 'Format',
    it: 'Formato',
    es: 'Formato',
    pt: 'Formato',
    nl: 'Formaat',
    pl: 'Format',
  },
  'reports.generateButton': {
    en: 'Generate',
    de: 'Erstellen',
    fr: 'Générer',
    it: 'Genera',
    es: 'Generar',
    pt: 'Gerar',
    nl: 'Genereren',
    pl: 'Generuj',
  },
  'reports.download': {
    en: 'Download',
    de: 'Herunterladen',
    fr: 'Télécharger',
    it: 'Scarica',
    es: 'Descargar',
    pt: 'Baixar',
    nl: 'Downloaden',
    pl: 'Pobierz',
  },
  'reports.status.queued': {
    en: 'Queued',
    de: 'In Warteschlange',
    fr: 'En file',
    it: 'In coda',
    es: 'En cola',
    pt: 'Na fila',
    nl: 'In wachtrij',
    pl: 'W kolejce',
  },
  'reports.status.done': {
    en: 'Done',
    de: 'Fertig',
    fr: 'Terminé',
    it: 'Fatto',
    es: 'Hecho',
    pt: 'Concluído',
    nl: 'Klaar',
    pl: 'Gotowe',
  },
  'reports.status.error': {
    en: 'Error',
    de: 'Fehler',
    fr: 'Erreur',
    it: 'Errore',
    es: 'Error',
    pt: 'Erro',
    nl: 'Fout',
    pl: 'Błąd',
  },
  'reports.type.customer_summary': {
    en: 'Customer summary',
    de: 'Kundenübersicht',
    fr: 'Résumé clients',
    it: 'Riepilogo clienti',
    es: 'Resumen de clientes',
    pt: 'Resumo de clientes',
    nl: 'Klantsamenvatting',
    pl: 'Podsumowanie klientów',
  },
  'reports.type.extinguisher_due': {
    en: 'Extinguishers due',
    de: 'Fällige Feuerlöscher',
    fr: 'Extincteurs à contrôler',
    it: 'Estintori in scadenza',
    es: 'Extintores pendientes',
    pt: 'Extintores pendentes',
    nl: 'Brandblussers vervallen',
    pl: 'Gaśnice do kontroli',
  },
  'reports.type.tenant_activity': {
    en: 'Tenant activity',
    de: 'Mandantenaktivität',
    fr: 'Activité locataire',
    it: 'Attività tenant',
    es: 'Actividad del inquilino',
    pt: 'Atividade do tenant',
    nl: 'Tenantactiviteit',
    pl: 'Aktywność najemcy',
  },
  'io.dropJson': {
    en: 'Drop JSON or click to browse',
    de: 'JSON ablegen oder durchsuchen',
    fr: 'Déposer JSON ou parcourir',
    it: 'Rilascia JSON o sfoglia',
    es: 'Soltar JSON o explorar',
    pt: 'Soltar JSON ou navegar',
    nl: 'JSON neerzetten of bladeren',
    pl: 'Upuść JSON lub przeglądaj',
  },
  'io.dataSummary': {
    en: 'Data Summary',
    de: 'Datenübersicht',
    fr: 'Résumé des données',
    it: 'Riepilogo dati',
    es: 'Resumen de datos',
    pt: 'Resumo dos dados',
    nl: 'Gegevensoverzicht',
    pl: 'Podsumowanie danych',
  },
  'common.search': {
    en: 'Search',
    de: 'Suche',
    fr: 'Rechercher',
    it: 'Cerca',
    es: 'Buscar',
    pt: 'Pesquisar',
    nl: 'Zoeken',
    pl: 'Szukaj',
  },
  'common.all': {
    en: 'All',
    de: 'Alle',
    fr: 'Tous',
    it: 'Tutti',
    es: 'Todos',
    pt: 'Todos',
    nl: 'Alles',
    pl: 'Wszystkie',
  },
  'common.copy': {
    en: 'Copy',
    de: 'Kopieren',
    fr: 'Copier',
    it: 'Copia',
    es: 'Copiar',
    pt: 'Copiar',
    nl: 'Kopiëren',
    pl: 'Kopiuj',
  },
  'common.yes': {
    en: 'Yes',
    de: 'Ja',
    fr: 'Oui',
    it: 'Sì',
    es: 'Sí',
    pt: 'Sim',
    nl: 'Ja',
    pl: 'Tak',
  },
  'common.no': {
    en: 'No',
    de: 'Nein',
    fr: 'Non',
    it: 'No',
    es: 'No',
    pt: 'Não',
    nl: 'Nee',
    pl: 'Nie',
  },
  'common.confirm': {
    en: 'Confirm',
    de: 'Bestätigen',
    fr: 'Confirmer',
    it: 'Conferma',
    es: 'Confirmar',
    pt: 'Confirmar',
    nl: 'Bevestigen',
    pl: 'Potwierdź',
  },
  'common.close': {
    en: 'Close',
    de: 'Schliessen',
    fr: 'Fermer',
    it: 'Chiudi',
    es: 'Cerrar',
    pt: 'Fechar',
    nl: 'Sluiten',
    pl: 'Zamknij',
  },
  'common.notSet': {
    en: 'Not set',
    de: 'Nicht gesetzt',
    fr: 'Non défini',
    it: 'Non impostato',
    es: 'No definido',
    pt: 'Não definido',
    nl: 'Niet ingesteld',
    pl: 'Nie ustawiono',
  },
  'common.country': {
    en: 'Country',
    de: 'Land',
    fr: 'Pays',
    it: 'Paese',
    es: 'País',
    pt: 'País',
    nl: 'Land',
    pl: 'Kraj',
  },

  // Status
  'status.done': {
    en: 'Done',
    de: 'Erledigt',
    fr: 'Terminé',
    it: 'Fatto',
    es: 'Hecho',
    pt: 'Concluído',
    nl: 'Klaar',
    pl: 'Gotowe',
  },
  'status.waiting': {
    en: 'Waiting',
    de: 'Noch warten',
    fr: 'En attente',
    it: 'In attesa',
    es: 'Esperando',
    pt: 'Aguardando',
    nl: 'Wachtend',
    pl: 'Oczekuje',
  },
  'status.todo': {
    en: 'To do',
    de: 'Steht an',
    fr: 'À faire',
    it: 'Da fare',
    es: 'Pendiente',
    pt: 'A fazer',
    nl: 'Te doen',
    pl: 'Do zrobienia',
  },
  'status.none': {
    en: 'No date',
    de: 'Kein Termin',
    fr: 'Pas de date',
    it: 'Nessuna data',
    es: 'Sin fecha',
    pt: 'Sem data',
    nl: 'Geen datum',
    pl: 'Brak daty',
  },

  // Overview
  'overview.title': {
    en: 'Customer Overview',
    de: 'Kundenübersicht',
    fr: 'Aperçu clients',
    it: 'Panoramica clienti',
    es: 'Resumen de clientes',
    pt: 'Visão geral de clientes',
    nl: 'Klantenoverzicht',
    pl: 'Przegląd klientów',
  },
  'overview.subtitle': {
    en: 'All sites with fire protection equipment',
    de: 'Alle Standorte der Löschgeräte',
    fr: 'Tous les sites avec équipement incendie',
    it: 'Tutti i siti con attrezzatura antincendio',
    es: 'Todos los sitios con equipos contra incendios',
    pt: 'Todos os locais com equipamento contra incêndio',
    nl: 'Alle locaties met brandbeveiligingsapparatuur',
    pl: 'Wszystkie lokalizacje ze sprzętem przeciwpożarowym',
  },
  'overview.selected': {
    en: 'selected',
    de: 'ausgewählt',
    fr: 'sélectionnés',
    it: 'selezionati',
    es: 'seleccionados',
    pt: 'selecionados',
    nl: 'geselecteerd',
    pl: 'wybranych',
  },
  'overview.clearFilters': {
    en: 'Clear filters',
    de: 'Filter löschen',
    fr: 'Effacer filtres',
    it: 'Cancella filtri',
    es: 'Borrar filtros',
    pt: 'Limpar filtros',
    nl: 'Filters wissen',
    pl: 'Wyczyść filtry',
  },
  'overview.mapDisplayed': {
    en: 'Map of displayed',
    de: 'Karte angezeigter Daten',
    fr: 'Carte affichée',
    it: 'Mappa visualizzata',
    es: 'Mapa mostrado',
    pt: 'Mapa exibido',
    nl: 'Weergegeven kaart',
    pl: 'Wyświetlana mapa',
  },
  'overview.selectAll': {
    en: 'Select all',
    de: 'Alle auswählen',
    fr: 'Tout sélectionner',
    it: 'Seleziona tutto',
    es: 'Seleccionar todo',
    pt: 'Selecionar tudo',
    nl: 'Alles selecteren',
    pl: 'Zaznacz wszystko',
  },
  'overview.deselectAll': {
    en: 'Deselect all',
    de: 'Alle abwählen',
    fr: 'Tout désélectionner',
    it: 'Deseleziona tutto',
    es: 'Deseleccionar todo',
    pt: 'Desmarcar tudo',
    nl: 'Alles deselecteren',
    pl: 'Odznacz wszystko',
  },

  // Customer
  'customer.info': {
    en: 'Info & Addresses',
    de: 'Info & Adressen',
    fr: 'Info & Adresses',
    it: 'Info & Indirizzi',
    es: 'Info y Direcciones',
    pt: 'Info e Endereços',
    nl: 'Info & Adressen',
    pl: 'Info i Adresy',
  },
  'customer.extinguishers': {
    en: 'Fire Extinguishers',
    de: 'Feuerlöscher',
    fr: 'Extincteurs',
    it: 'Estintori',
    es: 'Extintores',
    pt: 'Extintores',
    nl: 'Brandblussers',
    pl: 'Gaśnice',
  },
  'customer.hoses': {
    en: 'Fire Hoses',
    de: 'Löschposten',
    fr: 'Lances incendie',
    it: 'Manichette',
    es: 'Mangueras',
    pt: 'Mangueiras',
    nl: 'Brandslangen',
    pl: 'Węże strażackie',
  },
  'customer.history': {
    en: 'History',
    de: 'Verlauf',
    fr: 'Historique',
    it: 'Cronologia',
    es: 'Historial',
    pt: 'Histórico',
    nl: 'Geschiedenis',
    pl: 'Historia',
  },
  'customer.billingAddress': {
    en: 'Billing Address',
    de: 'Rechnungsadresse',
    fr: 'Adresse de facturation',
    it: 'Indirizzo fatturazione',
    es: 'Dirección de facturación',
    pt: 'Endereço de faturação',
    nl: 'Factuuradres',
    pl: 'Adres rozliczeniowy',
  },
  'customer.objectAddress': {
    en: 'Object / Site Address',
    de: 'Objekt / Standort',
    fr: 'Adresse du site',
    it: 'Indirizzo sito',
    es: 'Dirección del sitio',
    pt: 'Endereço do local',
    nl: 'Locatieadres',
    pl: 'Adres obiektu',
  },
  'customer.ownerAddress': {
    en: 'Owner Address',
    de: 'Eigentümer',
    fr: 'Adresse propriétaire',
    it: 'Indirizzo proprietario',
    es: 'Dirección del propietario',
    pt: 'Endereço do proprietário',
    nl: 'Eigenaaradres',
    pl: 'Adres właściciela',
  },
  'customer.contact': {
    en: 'Janitor / Contact',
    de: 'Abwart / Kontakt',
    fr: 'Concierge / Contact',
    it: 'Custode / Contatto',
    es: 'Conserje / Contacto',
    pt: 'Zelador / Contacto',
    nl: 'Conciërge / Contact',
    pl: 'Dozorca / Kontakt',
  },
  'customer.metadata': {
    en: 'Metadata',
    de: 'Administration',
    fr: 'Métadonnées',
    it: 'Metadati',
    es: 'Metadatos',
    pt: 'Metadados',
    nl: 'Metadata',
    pl: 'Metadane',
  },
  'customer.notes': {
    en: 'Notes',
    de: 'Notizen',
    fr: 'Notes',
    it: 'Note',
    es: 'Notas',
    pt: 'Notas',
    nl: 'Notities',
    pl: 'Notatki',
  },
  'customer.equipmentSummary': {
    en: 'Equipment Summary',
    de: 'Geräteübersicht',
    fr: 'Résumé des équipements',
    it: 'Riepilogo attrezzature',
    es: 'Resumen de equipos',
    pt: 'Resumo de equipamentos',
    nl: 'Apparatuuroverzicht',
    pl: 'Podsumowanie sprzętu',
  },
  'customer.created': {
    en: 'Created',
    de: 'Objekt erstellt',
    fr: 'Créé',
    it: 'Creato',
    es: 'Creado',
    pt: 'Criado',
    nl: 'Aangemaakt',
    pl: 'Utworzono',
  },
  'customer.createdBy': {
    en: 'Created by',
    de: 'Erstellt von',
    fr: 'Créé par',
    it: 'Creato da',
    es: 'Creado por',
    pt: 'Criado por',
    nl: 'Aangemaakt door',
    pl: 'Utworzono przez',
  },
  'customer.worker': {
    en: 'Worker',
    de: 'Servicetechniker',
    fr: 'Technicien',
    it: 'Tecnico',
    es: 'Técnico',
    pt: 'Técnico',
    nl: 'Medewerker',
    pl: 'Pracownik',
  },
  'customer.lastInspection': {
    en: 'Last Inspection',
    de: 'Letzte Kontrolle',
    fr: 'Dernière inspection',
    it: 'Ultima ispezione',
    es: 'Última inspección',
    pt: 'Última inspeção',
    nl: 'Laatste inspectie',
    pl: 'Ostatnia kontrola',
  },
  'customer.nextInspection': {
    en: 'Next Inspection',
    de: 'Nächste Kontrolle',
    fr: 'Prochaine inspection',
    it: 'Prossima ispezione',
    es: 'Próxima inspección',
    pt: 'Próxima inspeção',
    nl: 'Volgende inspectie',
    pl: 'Następna kontrola',
  },
  'customer.coordinates': {
    en: 'Coordinates',
    de: 'Koordinaten',
    fr: 'Coordonnées',
    it: 'Coordinate',
    es: 'Coordenadas',
    pt: 'Coordenadas',
    nl: 'Coördinaten',
    pl: 'Współrzędne',
  },
  'customer.archive': {
    en: 'Archive',
    de: 'Archiv erstellen',
    fr: 'Archiver',
    it: 'Archivia',
    es: 'Archivar',
    pt: 'Arquivar',
    nl: 'Archiveren',
    pl: 'Archiwizuj',
  },
  'customer.createArchive': {
    en: 'Create Archive',
    de: 'Archiv erstellen',
    fr: 'Créer archive',
    it: 'Crea archivio',
    es: 'Crear archivo',
    pt: 'Criar arquivo',
    nl: 'Archief aanmaken',
    pl: 'Utwórz archiwum',
  },

  // Extinguisher
  'ext.title': {
    en: 'Extinguisher',
    de: 'Feuerlöscher',
    fr: 'Extincteur',
    it: 'Estintore',
    es: 'Extintor',
    pt: 'Extintor',
    nl: 'Brandblusser',
    pl: 'Gaśnica',
  },
  'ext.manufacturer': {
    en: 'Manufacturer',
    de: 'Hersteller',
    fr: 'Fabricant',
    it: 'Produttore',
    es: 'Fabricante',
    pt: 'Fabricante',
    nl: 'Fabrikant',
    pl: 'Producent',
  },
  'ext.model': {
    en: 'Model',
    de: 'Modell',
    fr: 'Modèle',
    it: 'Modello',
    es: 'Modelo',
    pt: 'Modelo',
    nl: 'Model',
    pl: 'Model',
  },
  'ext.agentType': {
    en: 'Agent Type',
    de: 'Löschmittel',
    fr: 'Agent extincteur',
    it: 'Agente estinguente',
    es: 'Tipo de agente',
    pt: 'Tipo de agente',
    nl: 'Blusmiddel',
    pl: 'Środek gaśniczy',
  },
  'ext.content': {
    en: 'Content',
    de: 'Inhalt',
    fr: 'Contenu',
    it: 'Contenuto',
    es: 'Contenido',
    pt: 'Conteúdo',
    nl: 'Inhoud',
    pl: 'Zawartość',
  },
  'ext.type': {
    en: 'Type',
    de: 'Gruppe',
    fr: 'Type',
    it: 'Tipo',
    es: 'Tipo',
    pt: 'Tipo',
    nl: 'Type',
    pl: 'Typ',
  },
  'ext.pressure': {
    en: 'Pressure',
    de: 'Druck',
    fr: 'Pression',
    it: 'Pressione',
    es: 'Presión',
    pt: 'Pressão',
    nl: 'Druk',
    pl: 'Ciśnienie',
  },
  'ext.ageLimit': {
    en: 'Age limit',
    de: 'Altersgrenze',
    fr: "Limite d'âge",
    it: 'Limite di età',
    es: 'Límite de edad',
    pt: 'Limite de idade',
    nl: 'Leeftijdsgrens',
    pl: 'Limit wieku',
  },
  'ext.placement': {
    en: 'Placement',
    de: 'Standort',
    fr: 'Emplacement',
    it: 'Posizione',
    es: 'Ubicación',
    pt: 'Localização',
    nl: 'Plaatsing',
    pl: 'Lokalizacja',
  },
  'ext.floor': {
    en: 'Floor',
    de: 'Stockwerk',
    fr: 'Étage',
    it: 'Piano',
    es: 'Planta',
    pt: 'Andar',
    nl: 'Verdieping',
    pl: 'Piętro',
  },
  'ext.location': {
    en: 'Location',
    de: 'Standort',
    fr: 'Lieu',
    it: 'Luogo',
    es: 'Ubicación',
    pt: 'Local',
    nl: 'Locatie',
    pl: 'Miejsce',
  },
  'ext.idNumber': {
    en: 'Our ID Number',
    de: 'Nummer',
    fr: 'Numéro ID',
    it: 'Numero ID',
    es: 'Número ID',
    pt: 'Número ID',
    nl: 'ID Nummer',
    pl: 'Numer ID',
  },
  'ext.year': {
    en: 'Manufacturing Year',
    de: 'Jahrgang',
    fr: 'Année de fabrication',
    it: 'Anno di produzione',
    es: 'Año de fabricación',
    pt: 'Ano de fabrico',
    nl: 'Bouwjaar',
    pl: 'Rok produkcji',
  },
  'ext.inspectionDates': {
    en: 'Inspection Dates',
    de: 'Kontrolldaten',
    fr: "Dates d'inspection",
    it: 'Date di ispezione',
    es: 'Fechas de inspección',
    pt: 'Datas de inspeção',
    nl: 'Inspectiedata',
    pl: 'Daty kontroli',
  },
  'ext.lastInspection': {
    en: 'Last Inspection',
    de: 'Letzte Kontrolle',
    fr: 'Dernière inspection',
    it: 'Ultima ispezione',
    es: 'Última inspección',
    pt: 'Última inspeção',
    nl: 'Laatste inspectie',
    pl: 'Ostatnia kontrola',
  },
  'ext.nextInspection': {
    en: 'Next Inspection',
    de: 'Nächste Kontrolle',
    fr: 'Prochaine inspection',
    it: 'Prossima ispezione',
    es: 'Próxima inspección',
    pt: 'Próxima inspeção',
    nl: 'Volgende inspectie',
    pl: 'Następna kontrola',
  },
  'ext.refilled': {
    en: 'Charge Replaced',
    de: 'Füllung ersetzt am',
    fr: 'Charge remplacée',
    it: 'Carica sostituita',
    es: 'Carga reemplazada',
    pt: 'Carga substituída',
    nl: 'Lading vervangen',
    pl: 'Ładunek wymieniony',
  },
  'ext.billing': {
    en: 'Billing Type',
    de: 'Rechnungstyp',
    fr: 'Type de facturation',
    it: 'Tipo fatturazione',
    es: 'Tipo de facturación',
    pt: 'Tipo de faturação',
    nl: 'Facturatietype',
    pl: 'Typ rozliczenia',
  },
  'ext.billingNone': {
    en: 'No entry',
    de: 'Kein Eintrag',
    fr: 'Aucune entrée',
    it: 'Nessuna voce',
    es: 'Sin entrada',
    pt: 'Sem entrada',
    nl: 'Geen invoer',
    pl: 'Brak wpisu',
  },
  'ext.billingNew': {
    en: 'New device',
    de: 'Neues Gerät',
    fr: 'Nouvel appareil',
    it: 'Nuovo dispositivo',
    es: 'Dispositivo nuevo',
    pt: 'Novo dispositivo',
    nl: 'Nieuw apparaat',
    pl: 'Nowe urządzenie',
  },
  'ext.billingMaintenance': {
    en: 'Maintenance',
    de: 'Materialkontrolle',
    fr: 'Maintenance',
    it: 'Manutenzione',
    es: 'Mantenimiento',
    pt: 'Manutenção',
    nl: 'Onderhoud',
    pl: 'Konserwacja',
  },
  'ext.billingDisposed': {
    en: 'Disposed',
    de: 'Entsorgt',
    fr: 'Éliminé',
    it: 'Smaltito',
    es: 'Eliminado',
    pt: 'Descartado',
    nl: 'Afgevoerd',
    pl: 'Zutylizowano',
  },
  'ext.spareParts': {
    en: 'Spare Parts',
    de: 'Ersatzteile',
    fr: 'Pièces de rechange',
    it: 'Ricambi',
    es: 'Repuestos',
    pt: 'Peças',
    nl: 'Onderdelen',
    pl: 'Części zamienne',
  },
  'ext.ageWarning': {
    en: 'exceeds age limit',
    de: 'überschreitet Altersgrenze',
    fr: "dépasse la limite d'âge",
    it: 'supera il limite di età',
    es: 'supera el límite de edad',
    pt: 'excede limite de idade',
    nl: 'overschrijdt leeftijdsgrens',
    pl: 'przekracza limit wieku',
  },
  'ext.documentation': {
    en: 'Documentation',
    de: 'Dokumentation',
    fr: 'Documentation',
    it: 'Documentazione',
    es: 'Documentación',
    pt: 'Documentação',
    nl: 'Documentatie',
    pl: 'Dokumentacja',
  },

  // Invoice
  'invoice.title': {
    en: 'Invoice',
    de: 'Rechnung',
    fr: 'Facture',
    it: 'Fattura',
    es: 'Factura',
    pt: 'Fatura',
    nl: 'Factuur',
    pl: 'Faktura',
  },
  'invoice.billTo': {
    en: 'Bill to',
    de: 'Rechnung an',
    fr: 'Facturer à',
    it: 'Fatturare a',
    es: 'Facturar a',
    pt: 'Faturar para',
    nl: 'Factureren aan',
    pl: 'Faktura dla',
  },
  'invoice.object': {
    en: 'Object',
    de: 'Objekt',
    fr: 'Objet',
    it: 'Oggetto',
    es: 'Objeto',
    pt: 'Objeto',
    nl: 'Object',
    pl: 'Obiekt',
  },
  'invoice.owner': {
    en: 'Owner',
    de: 'Eigentümer',
    fr: 'Propriétaire',
    it: 'Proprietario',
    es: 'Propietario',
    pt: 'Proprietário',
    nl: 'Eigenaar',
    pl: 'Właściciel',
  },
  'invoice.article': {
    en: 'Article-Nr.',
    de: 'Artikel-Nr.',
    fr: 'N° article',
    it: 'N° articolo',
    es: 'N° artículo',
    pt: 'N° artigo',
    nl: 'Artikelnr.',
    pl: 'Nr artykułu',
  },
  'invoice.description': {
    en: 'Description',
    de: 'Beschreibung',
    fr: 'Description',
    it: 'Descrizione',
    es: 'Descripción',
    pt: 'Descrição',
    nl: 'Beschrijving',
    pl: 'Opis',
  },
  'invoice.qty': {
    en: 'Qty',
    de: 'Anzahl',
    fr: 'Qté',
    it: 'Qtà',
    es: 'Cant.',
    pt: 'Qtd.',
    nl: 'Aantal',
    pl: 'Ilość',
  },
  'invoice.price': {
    en: 'Price',
    de: 'Preis',
    fr: 'Prix',
    it: 'Prezzo',
    es: 'Precio',
    pt: 'Preço',
    nl: 'Prijs',
    pl: 'Cena',
  },
  'invoice.total': {
    en: 'Total',
    de: 'Total',
    fr: 'Total',
    it: 'Totale',
    es: 'Total',
    pt: 'Total',
    nl: 'Totaal',
    pl: 'Suma',
  },
  'invoice.subtotal': {
    en: 'Subtotal',
    de: 'Zwischensumme',
    fr: 'Sous-total',
    it: 'Subtotale',
    es: 'Subtotal',
    pt: 'Subtotal',
    nl: 'Subtotaal',
    pl: 'Suma częściowa',
  },
  'invoice.vat': {
    en: 'VAT',
    de: 'MwSt.',
    fr: 'TVA',
    it: 'IVA',
    es: 'IVA',
    pt: 'IVA',
    nl: 'BTW',
    pl: 'VAT',
  },
  'invoice.totalIncl': {
    en: 'Total incl. VAT',
    de: 'Total inkl. MwSt.',
    fr: 'Total TTC',
    it: 'Totale IVA incl.',
    es: 'Total con IVA',
    pt: 'Total c/ IVA',
    nl: 'Totaal incl. BTW',
    pl: 'Suma z VAT',
  },
  'invoice.totalExcl': {
    en: 'Total excl. VAT',
    de: 'Total exkl. MwSt.',
    fr: 'Total HT',
    it: 'Totale IVA escl.',
    es: 'Total sin IVA',
    pt: 'Total s/ IVA',
    nl: 'Totaal excl. BTW',
    pl: 'Suma bez VAT',
  },
  'invoice.rounding': {
    en: 'Rounding difference',
    de: 'Rundungsdifferenz',
    fr: "Différence d'arrondi",
    it: 'Differenza di arrotondamento',
    es: 'Diferencia de redondeo',
    pt: 'Diferença de arredondamento',
    nl: 'Afrondingsverschil',
    pl: 'Różnica zaokrąglenia',
  },
  'invoice.date': {
    en: 'Date',
    de: 'Datum',
    fr: 'Date',
    it: 'Data',
    es: 'Fecha',
    pt: 'Data',
    nl: 'Datum',
    pl: 'Data',
  },
  'invoice.due': {
    en: 'Due',
    de: 'Fällig',
    fr: 'Échéance',
    it: 'Scadenza',
    es: 'Vencimiento',
    pt: 'Vencimento',
    nl: 'Vervaldatum',
    pl: 'Termin',
  },
  'invoice.paymentTerms': {
    en: 'Payment terms',
    de: 'Zahlungsfrist',
    fr: 'Conditions de paiement',
    it: 'Termini di pagamento',
    es: 'Condiciones de pago',
    pt: 'Condições de pagamento',
    nl: 'Betalingsvoorwaarden',
    pl: 'Warunki płatności',
  },
  'invoice.thanks': {
    en: 'Thank you for your order and your trust.',
    de: 'Vielen Dank für den Auftrag und das Vertrauen.',
    fr: 'Merci pour votre commande et votre confiance.',
    it: "Grazie per l'ordine e la fiducia.",
    es: 'Gracias por su pedido y su confianza.',
    pt: 'Obrigado pelo pedido e pela confiança.',
    nl: 'Bedankt voor uw bestelling en uw vertrouwen.',
    pl: 'Dziękujemy za zamówienie i zaufanie.',
  },
  'invoice.daysNet': {
    en: 'days net',
    de: 'Tage netto',
    fr: 'jours net',
    it: 'giorni netti',
    es: 'días neto',
    pt: 'dias líquidos',
    nl: 'dagen netto',
    pl: 'dni netto',
  },
  'invoice.maintenance': {
    en: 'Maintenance',
    de: 'Wartung',
    fr: 'Maintenance',
    it: 'Manutenzione',
    es: 'Mantenimiento',
    pt: 'Manutenção',
    nl: 'Onderhoud',
    pl: 'Konserwacja',
  },
  'invoice.hoseMaintenance': {
    en: 'Hose Maintenance',
    de: 'Wartung Löschposten',
    fr: 'Maintenance lances',
    it: 'Manutenzione manichette',
    es: 'Mantenimiento mangueras',
    pt: 'Manutenção mangueiras',
    nl: 'Slangonderhoud',
    pl: 'Konserwacja węży',
  },
  'invoice.fireHoses': {
    en: 'Fire Hoses',
    de: 'Löschposten',
    fr: 'Lances incendie',
    it: 'Manichette',
    es: 'Mangueras',
    pt: 'Mangueiras',
    nl: 'Brandslangen',
    pl: 'Węże strażackie',
  },
  'invoice.unit': {
    en: 'pcs.',
    de: 'Stk.',
    fr: 'pcs',
    it: 'pz.',
    es: 'uds.',
    pt: 'pç.',
    nl: 'st.',
    pl: 'szt.',
  },
  'invoice.travelSurcharge': {
    en: 'Travel surcharge',
    de: 'Anfahrtspauschale',
    fr: 'Forfait déplacement',
    it: 'Supplemento viaggio',
    es: 'Recargo desplazamiento',
    pt: 'Taxa deslocação',
    nl: 'Reistoeslag',
    pl: 'Ryczałt dojazd',
  },
  'invoice.attn': {
    en: 'Attn.',
    de: 'z.Hd.',
    fr: "À l'att.",
    it: 'C.A.',
    es: 'A/A',
    pt: 'A/C',
    nl: 'T.a.v.',
    pl: 'Do wiad.',
  },
  'invoice.vatOverride': {
    en: 'VAT %',
    de: 'MwSt. %',
    fr: 'TVA %',
    it: 'IVA %',
    es: 'IVA %',
    pt: 'IVA %',
    nl: 'BTW %',
    pl: 'VAT %',
  },

  // Settings
  'settings.title': {
    en: 'Settings',
    de: 'Einstellungen',
    fr: 'Paramètres',
    it: 'Impostazioni',
    es: 'Configuración',
    pt: 'Configurações',
    nl: 'Instellingen',
    pl: 'Ustawienia',
  },
  'settings.subtitle': {
    en: 'Company, billing, and inspection rules',
    de: 'Firma, Abrechnung und Kontrollregeln',
    fr: "Entreprise, facturation et règles d'inspection",
    it: 'Azienda, fatturazione e regole di ispezione',
    es: 'Empresa, facturación y reglas de inspección',
    pt: 'Empresa, faturação e regras de inspeção',
    nl: 'Bedrijf, facturatie en inspectieregels',
    pl: 'Firma, rozliczenia i zasady kontroli',
  },
  'settings.company': {
    en: 'Company',
    de: 'Firma',
    fr: 'Entreprise',
    it: 'Azienda',
    es: 'Empresa',
    pt: 'Empresa',
    nl: 'Bedrijf',
    pl: 'Firma',
  },
  'settings.financial': {
    en: 'Financial',
    de: 'Finanzen',
    fr: 'Finances',
    it: 'Finanze',
    es: 'Finanzas',
    pt: 'Finanças',
    nl: 'Financieel',
    pl: 'Finanse',
  },
  'settings.inspectionRules': {
    en: 'Inspection Rules',
    de: 'Kontrollregeln',
    fr: "Règles d'inspection",
    it: 'Regole di ispezione',
    es: 'Reglas de inspección',
    pt: 'Regras de inspeção',
    nl: 'Inspectieregels',
    pl: 'Zasady kontroli',
  },

  // Address dialog
  'address.companyName': {
    en: 'Company / Name',
    de: 'Firma / Name',
    fr: 'Société / Nom',
    it: 'Azienda / Nome',
    es: 'Empresa / Nombre',
    pt: 'Empresa / Nome',
    nl: 'Bedrijf / Naam',
    pl: 'Firma / Nazwa',
  },
  'address.attn': {
    en: 'Attn / c/o',
    de: 'z.Hd.',
    fr: "À l'att. de",
    it: 'C.A.',
    es: 'A la att. de',
    pt: 'A/C',
    nl: 'T.a.v.',
    pl: 'Do rąk',
  },
  'address.street': {
    en: 'Street',
    de: 'Strasse',
    fr: 'Rue',
    it: 'Via',
    es: 'Calle',
    pt: 'Rua',
    nl: 'Straat',
    pl: 'Ulica',
  },
  'address.zip': {
    en: 'ZIP',
    de: 'PLZ',
    fr: 'Code postal',
    it: 'CAP',
    es: 'C.P.',
    pt: 'CEP',
    nl: 'Postcode',
    pl: 'Kod pocztowy',
  },
  'address.city': {
    en: 'City',
    de: 'Ort',
    fr: 'Ville',
    it: 'Città',
    es: 'Ciudad',
    pt: 'Cidade',
    nl: 'Stad',
    pl: 'Miasto',
  },
  'address.phone': {
    en: 'Phone',
    de: 'Telefon',
    fr: 'Téléphone',
    it: 'Telefono',
    es: 'Teléfono',
    pt: 'Telefone',
    nl: 'Telefoon',
    pl: 'Telefon',
  },
  'address.email': {
    en: 'Email',
    de: 'E-Mail',
    fr: 'E-mail',
    it: 'Email',
    es: 'Correo',
    pt: 'E-mail',
    nl: 'E-mail',
    pl: 'E-mail',
  },
  'address.enterDetails': {
    en: 'Enter address details. Use Geocode to resolve WGS84 coordinates.',
    de: 'Adresse eingeben. Geocode für WGS84-Koordinaten nutzen.',
    fr: "Saisir l'adresse. Géocodage pour les coordonnées WGS84.",
    it: 'Inserisci indirizzo. Geocodifica per coordinate WGS84.',
    es: 'Introduzca la dirección. Geocodificar para coordenadas WGS84.',
    pt: 'Insira o endereço. Geocodificar para coordenadas WGS84.',
    nl: 'Voer adresgegevens in. Geocodeer voor WGS84-coördinaten.',
    pl: 'Wprowadź adres. Geokoduj, aby uzyskać współrzędne WGS84.',
  },
  'address.geocodeSave': {
    en: 'Geocode & Save',
    de: 'Geocode & Speichern',
    fr: 'Géocoder & Enregistrer',
    it: 'Geocodifica & Salva',
    es: 'Geocodificar & Guardar',
    pt: 'Geocodificar & Salvar',
    nl: 'Geocodeer & Opslaan',
    pl: 'Geokoduj & Zapisz',
  },
  'address.select': {
    en: '— Select —',
    de: '— Auswählen —',
    fr: '— Sélectionner —',
    it: '— Seleziona —',
    es: '— Seleccionar —',
    pt: '— Selecionar —',
    nl: '— Selecteer —',
    pl: '— Wybierz ���',
  },

  // Customer detail (extra keys)
  'customer.notFound': {
    en: 'Customer not found.',
    de: 'Kunde nicht gefunden.',
    fr: 'Client introuvable.',
    it: 'Cliente non trovato.',
    es: 'Cliente no encontrado.',
    pt: 'Cliente não encontrado.',
    nl: 'Klant niet gevonden.',
    pl: 'Klient nie znaleziony.',
  },
  'customer.deleteConfirm': {
    en: 'Delete customer',
    de: 'Kunde löschen',
    fr: 'Supprimer client',
    it: 'Eliminare cliente',
    es: 'Eliminar cliente',
    pt: 'Excluir cliente',
    nl: 'Klant verwijderen',
    pl: 'Usunąć klienta',
  },
  'customer.deleted': {
    en: 'Deleted',
    de: 'Gelöscht',
    fr: 'Supprimé',
    it: 'Eliminato',
    es: 'Eliminado',
    pt: 'Excluído',
    nl: 'Verwijderd',
    pl: 'Usunięto',
  },
  'customer.archiveCreated': {
    en: 'Archive snapshot created',
    de: 'Archiv-Snapshot erstellt',
    fr: 'Archive créée',
    it: 'Archivio creato',
    es: 'Archivo creado',
    pt: 'Arquivo criado',
    nl: 'Archief aangemaakt',
    pl: 'Archiwum utworzone',
  },
  'customer.coordsResolved': {
    en: 'Coordinates resolved',
    de: 'Koordinaten aufgelöst',
    fr: 'Coordonnées résolues',
    it: 'Coordinate risolte',
    es: 'Coordenadas resueltas',
    pt: 'Coordenadas resolvidas',
    nl: 'Coördinaten opgelost',
    pl: 'Współrzędne rozwiązane',
  },
  'customer.addressSaved': {
    en: 'Address saved',
    de: 'Adresse gespeichert',
    fr: 'Adresse enregistrée',
    it: 'Indirizzo salvato',
    es: 'Dirección guardada',
    pt: 'Endereço salvo',
    nl: 'Adres opgeslagen',
    pl: 'Adres zapisany',
  },
  'customer.copied': {
    en: 'Copied',
    de: 'Kopiert',
    fr: 'Copié',
    it: 'Copiato',
    es: 'Copiado',
    pt: 'Copiado',
    nl: 'Gekopieerd',
    pl: 'Skopiowano',
  },
  'customer.addNotes': {
    en: 'Add notes for this customer...',
    de: 'Notizen für diesen Kunden...',
    fr: 'Notes pour ce client...',
    it: 'Note per questo cliente...',
    es: 'Notas para este cliente...',
    pt: 'Notas para este cliente...',
    nl: 'Notities voor deze klant...',
    pl: 'Notatki dla tego klienta...',
  },
  'customer.noEquipment': {
    en: 'No equipment',
    de: 'Keine Geräte',
    fr: 'Aucun équipement',
    it: 'Nessuna attrezzatura',
    es: 'Sin equipos',
    pt: 'Sem equipamento',
    nl: 'Geen apparatuur',
    pl: 'Brak sprzętu',
  },
  'customer.sortedByFloor': {
    en: 'Sorted by floor / location',
    de: 'Sortiert nach Stockwerk / Standort',
    fr: 'Trié par étage / lieu',
    it: 'Ordinato per piano / luogo',
    es: 'Ordenado por planta / ubicación',
    pt: 'Ordenado por andar / local',
    nl: 'Gesorteerd op verdieping / locatie',
    pl: 'Sortowanie wg piętra / lokalizacji',
  },
  'customer.noExtinguishers': {
    en: 'No extinguishers yet',
    de: 'Noch keine Feuerlöscher',
    fr: "Pas encore d'extincteurs",
    it: 'Nessun estintore',
    es: 'Sin extintores aún',
    pt: 'Sem extintores ainda',
    nl: 'Nog geen brandblussers',
    pl: 'Brak gaśnic',
  },
  'customer.noHoses': {
    en: 'No hoses yet',
    de: 'Noch keine Löschposten',
    fr: 'Pas encore de lances',
    it: 'Nessuna manichetta',
    es: 'Sin mangueras aún',
    pt: 'Sem mangueiras ainda',
    nl: 'Nog geen slangen',
    pl: 'Brak węży',
  },
  'customer.noArchives': {
    en: 'No archives yet',
    de: 'Noch keine Archive',
    fr: "Pas encore d'archives",
    it: 'Nessun archivio',
    es: 'Sin archivos aún',
    pt: 'Sem arquivos ainda',
    nl: 'Nog geen archieven',
    pl: 'Brak archiwów',
  },
  'customer.readOnlySnapshots': {
    en: 'Read-only snapshots of past inspections',
    de: 'Schreibgeschützte Snapshots vergangener Kontrollen',
    fr: 'Instantanés en lecture seule',
    it: 'Istantanee in sola lettura',
    es: 'Instantáneas de inspecciones pasadas',
    pt: 'Instantâneos de inspeções passadas',
    nl: 'Alleen-lezen snapshots',
    pl: 'Migawki poprzednich kontroli',
  },
  'customer.devices': {
    en: 'devices',
    de: 'Geräte',
    fr: 'appareils',
    it: 'dispositivi',
    es: 'dispositivos',
    pt: 'dispositivos',
    nl: 'apparaten',
    pl: 'urządzeń',
  },
  'customer.tel': {
    en: 'Tel:',
    de: 'Tel:',
    fr: 'Tél:',
    it: 'Tel:',
    es: 'Tel:',
    pt: 'Tel:',
    nl: 'Tel:',
    pl: 'Tel:',
  },

  // Table headers (shared)
  'table.floor': {
    en: 'Floor',
    de: 'Stockwerk',
    fr: 'Étage',
    it: 'Piano',
    es: 'Planta',
    pt: 'Andar',
    nl: 'Verdieping',
    pl: 'Piętro',
  },
  'table.location': {
    en: 'Location',
    de: 'Standort',
    fr: 'Lieu',
    it: 'Luogo',
    es: 'Ubicación',
    pt: 'Local',
    nl: 'Locatie',
    pl: 'Miejsce',
  },
  'table.mfrModel': {
    en: 'Mfr / Model',
    de: 'Hersteller / Modell',
    fr: 'Fabricant / Modèle',
    it: 'Produttore / Modello',
    es: 'Fabricante / Modelo',
    pt: 'Fabricante / Modelo',
    nl: 'Fabrikant / Model',
    pl: 'Producent / Model',
  },
  'table.type': {
    en: 'Type',
    de: 'Typ',
    fr: 'Type',
    it: 'Tipo',
    es: 'Tipo',
    pt: 'Tipo',
    nl: 'Type',
    pl: 'Typ',
  },
  'table.id': {
    en: 'ID',
    de: 'Nr.',
    fr: 'ID',
    it: 'ID',
    es: 'ID',
    pt: 'ID',
    nl: 'ID',
    pl: 'ID',
  },
  'table.year': {
    en: 'Year',
    de: 'Jahr',
    fr: 'Année',
    it: 'Anno',
    es: 'Año',
    pt: 'Ano',
    nl: 'Jaar',
    pl: 'Rok',
  },
  'table.last': {
    en: 'Last',
    de: 'Letzte',
    fr: 'Dernière',
    it: 'Ultima',
    es: 'Última',
    pt: 'Última',
    nl: 'Laatste',
    pl: 'Ostatnia',
  },
  'table.next': {
    en: 'Next',
    de: 'Nächste',
    fr: 'Prochaine',
    it: 'Prossima',
    es: 'Próxima',
    pt: 'Próxima',
    nl: 'Volgende',
    pl: 'Następna',
  },
  'table.billing': {
    en: 'Billing',
    de: 'Abrechnung',
    fr: 'Facturation',
    it: 'Fatturazione',
    es: 'Facturación',
    pt: 'Faturação',
    nl: 'Facturatie',
    pl: 'Rozliczenie',
  },
  'table.actions': {
    en: 'Actions',
    de: 'Aktionen',
    fr: 'Actions',
    it: 'Azioni',
    es: 'Acciones',
    pt: 'Ações',
    nl: 'Acties',
    pl: 'Akcje',
  },
  'table.name': {
    en: 'Name',
    de: 'Name',
    fr: 'Nom',
    it: 'Nome',
    es: 'Nombre',
    pt: 'Nome',
    nl: 'Naam',
    pl: 'Nazwa',
  },
  'table.role': {
    en: 'Role',
    de: 'Rolle',
    fr: 'Rôle',
    it: 'Ruolo',
    es: 'Rol',
    pt: 'Função',
    nl: 'Rol',
    pl: 'Rola',
  },
  'table.status': {
    en: 'Status',
    de: 'Status',
    fr: 'Statut',
    it: 'Stato',
    es: 'Estado',
    pt: 'Estado',
    nl: 'Status',
    pl: 'Status',
  },
  'table.avatar': {
    en: 'Avatar',
    de: 'Avatar',
    fr: 'Avatar',
    it: 'Avatar',
    es: 'Avatar',
    pt: 'Avatar',
    nl: 'Avatar',
    pl: 'Avatar',
  },
  'table.articleNr': {
    en: 'Article #',
    de: 'Artikel-Nr.',
    fr: 'N° article',
    it: 'N° articolo',
    es: 'N° artículo',
    pt: 'N° artigo',
    nl: 'Artikelnr.',
    pl: 'Nr artykułu',
  },
  'table.compatible': {
    en: 'Compatible',
    de: 'Kompatibel',
    fr: 'Compatible',
    it: 'Compatibile',
    es: 'Compatible',
    pt: 'Compatível',
    nl: 'Compatibel',
    pl: 'Kompatybilne',
  },
  'table.price': {
    en: 'Price',
    de: 'Preis',
    fr: 'Prix',
    it: 'Prezzo',
    es: 'Precio',
    pt: 'Preço',
    nl: 'Prijs',
    pl: 'Cena',
  },
  'table.code': {
    en: 'Code',
    de: 'Code',
    fr: 'Code',
    it: 'Codice',
    es: 'Código',
    pt: 'Código',
    nl: 'Code',
    pl: 'Kod',
  },
  'table.german': {
    en: 'German',
    de: 'Deutsch',
    fr: 'Allemand',
    it: 'Tedesco',
    es: 'Alemán',
    pt: 'Alemão',
    nl: 'Duits',
    pl: 'Niemiecki',
  },
  'table.english': {
    en: 'English',
    de: 'Englisch',
    fr: 'Anglais',
    it: 'Inglese',
    es: 'Inglés',
    pt: 'Inglês',
    nl: 'Engels',
    pl: 'Angielski',
  },
  'table.deviceType': {
    en: 'Device Type',
    de: 'Gerätetyp',
    fr: "Type d'appareil",
    it: 'Tipo dispositivo',
    es: 'Tipo de dispositivo',
    pt: 'Tipo de dispositivo',
    nl: 'Apparaattype',
    pl: 'Typ urządzenia',
  },
  'table.description': {
    en: 'Description',
    de: 'Beschreibung',
    fr: 'Description',
    it: 'Descrizione',
    es: 'Descripción',
    pt: 'Descrição',
    nl: 'Beschrijving',
    pl: 'Opis',
  },

  // Hose edit
  'hose.fireHose': {
    en: 'Fire Hose',
    de: 'Löschposten',
    fr: 'Lance incendie',
    it: 'Manichetta',
    es: 'Manguera',
    pt: 'Mangueira',
    nl: 'Brandslang',
    pl: 'Wąż strażacki',
  },
  'hose.equipment': {
    en: 'Equipment',
    de: 'Ausrüstung',
    fr: 'Équipement',
    it: 'Attrezzatura',
    es: 'Equipo',
    pt: 'Equipamento',
    nl: 'Apparatuur',
    pl: 'Sprzęt',
  },
  'hose.length': {
    en: 'Length (m)',
    de: 'Länge (m)',
    fr: 'Longueur (m)',
    it: 'Lunghezza (m)',
    es: 'Longitud (m)',
    pt: 'Comprimento (m)',
    nl: 'Lengte (m)',
    pl: 'Długość (m)',
  },
  'hose.diameter': {
    en: 'Diameter',
    de: 'Durchmesser',
    fr: 'Diamètre',
    it: 'Diametro',
    es: 'Diámetro',
    pt: 'Diâmetro',
    nl: 'Diameter',
    pl: 'Średnica',
  },
  'hose.connection': {
    en: 'Connection',
    de: 'Anschluss',
    fr: 'Raccord',
    it: 'Connessione',
    es: 'Conexión',
    pt: 'Conexão',
    nl: 'Aansluiting',
    pl: 'Złączka',
  },
  'hose.conditionChecks': {
    en: 'Condition Checks',
    de: 'Zustandsprüfungen',
    fr: "Contrôles d'état",
    it: 'Controlli condizione',
    es: 'Comprobaciones de estado',
    pt: 'Verificações de condição',
    nl: 'Conditiecontroles',
    pl: 'Kontrole stanu',
  },
  'hose.ok': {
    en: 'OK',
    de: 'OK',
    fr: 'OK',
    it: 'OK',
    es: 'OK',
    pt: 'OK',
    nl: 'OK',
    pl: 'OK',
  },
  'hose.notOk': {
    en: 'Not OK',
    de: 'Nicht OK',
    fr: 'Non OK',
    it: 'Non OK',
    es: 'No OK',
    pt: 'Não OK',
    nl: 'Niet OK',
    pl: 'Nie OK',
  },
  'hose.serviceReport': {
    en: 'Service Report',
    de: 'Servicebericht',
    fr: 'Rapport de service',
    it: 'Rapporto di servizio',
    es: 'Informe de servicio',
    pt: 'Relatório de serviço',
    nl: 'Servicerapport',
    pl: 'Raport serwisowy',
  },
  'hose.serviceDate': {
    en: 'Service Date',
    de: 'Servicedatum',
    fr: 'Date de service',
    it: 'Data servizio',
    es: 'Fecha de servicio',
    pt: 'Data de serviço',
    nl: 'Servicedatum',
    pl: 'Data serwisu',
  },
  'hose.nextService': {
    en: 'Next Service',
    de: 'Nächster Service',
    fr: 'Prochain service',
    it: 'Prossimo servizio',
    es: 'Próximo servicio',
    pt: 'Próximo serviço',
    nl: 'Volgende service',
    pl: 'Następny serwis',
  },
  'hose.restingPressure': {
    en: 'Resting Pressure',
    de: 'Ruhedruck',
    fr: 'Pression au repos',
    it: 'Pressione di riposo',
    es: 'Presión en reposo',
    pt: 'Pressão de repouso',
    nl: 'Rustdruk',
    pl: 'Ciśnienie spoczynkowe',
  },
  'hose.flowPressure': {
    en: 'Flow Pressure',
    de: 'Fliessdruck',
    fr: 'Pression de débit',
    it: 'Pressione di flusso',
    es: 'Presión de flujo',
    pt: 'Pressão de fluxo',
    nl: 'Stroomdruk',
    pl: 'Ciśnienie przepływu',
  },
  'hose.flowRate': {
    en: 'Flow Rate',
    de: 'Durchfluss',
    fr: 'Débit',
    it: 'Portata',
    es: 'Caudal',
    pt: 'Vazão',
    nl: 'Debiet',
    pl: 'Przepływ',
  },
  'hose.pressureTestDate': {
    en: '12-Bar Test Date',
    de: '12-Bar Prüfdatum',
    fr: 'Date test 12 bar',
    it: 'Data test 12 bar',
    es: 'Fecha prueba 12 bar',
    pt: 'Data teste 12 bar',
    nl: '12-bar testdatum',
    pl: 'Data testu 12 bar',
  },
  'hose.serviceTechnician': {
    en: 'Service Technician',
    de: 'Servicetechniker',
    fr: 'Technicien',
    it: 'Tecnico',
    es: 'Técnico',
    pt: 'Técnico',
    nl: 'Servicemonteur',
    pl: 'Serwisant',
  },
  'hose.hoseStandard': {
    en: 'Per SN EN 671-3: annual inspection + 5-year pressure test',
    de: 'Gemäss SN EN 671-3: jährliche Kontrolle + 5-jährige Druckprüfung',
    fr: 'Selon SN EN 671-3 : inspection annuelle + test de pression tous les 5 ans',
    it: 'Secondo SN EN 671-3: ispezione annuale + test pressione ogni 5 anni',
    es: 'Según SN EN 671-3: inspección anual + prueba de presión cada 5 años',
    pt: 'Conforme SN EN 671-3: inspeção anual + teste de pressão a cada 5 anos',
    nl: 'Volgens SN EN 671-3: jaarlijkse inspectie + 5-jaarlijkse druktest',
    pl: 'Wg SN EN 671-3: roczna kontrola + test ciśnieniowy co 5 lat',
  },
  'hose.addHose': {
    en: 'Add Hose',
    de: 'Löschposten hinzufügen',
    fr: 'Ajouter lance',
    it: 'Aggiungi manichetta',
    es: 'Añadir manguera',
    pt: 'Adicionar mangueira',
    nl: 'Slang toevoegen',
    pl: 'Dodaj wąż',
  },
  'hose.condition': {
    en: 'Condition',
    de: 'Zustand',
    fr: 'État',
    it: 'Condizione',
    es: 'Condición',
    pt: 'Condição',
    nl: 'Conditie',
    pl: 'Stan',
  },
  'hose.service': {
    en: 'Service',
    de: 'Service',
    fr: 'Service',
    it: 'Servizio',
    es: 'Servicio',
    pt: 'Serviço',
    nl: 'Service',
    pl: 'Serwis',
  },

  // Hose Options admin
  'hoseOptions.title': {
    en: 'Hose Options',
    de: 'Schlauchoptionen',
    fr: 'Options tuyaux',
    it: 'Opzioni manichetta',
    es: 'Opciones manguera',
    pt: 'Opções mangueira',
    nl: 'Slangopties',
    pl: 'Opcje węży',
  },
  'hoseOptions.subtitle': {
    en: 'Configure dropdown options for fire hose forms',
    de: 'Dropdown-Optionen für Löschposten-Formulare konfigurieren',
    fr: 'Configurer les options des formulaires lance incendie',
    it: 'Configura le opzioni dei moduli manichetta',
    es: 'Configurar opciones de formularios de manguera',
    pt: 'Configurar opções dos formulários de mangueira',
    nl: 'Dropdown-opties configureren voor brandslangformulieren',
    pl: 'Konfiguruj opcje formularzy węży strażackich',
  },
  'hoseOptions.hoseDiameters': {
    en: 'Hose Diameters',
    de: 'Schlauchdurchmesser',
    fr: 'Diamètres tuyaux',
    it: 'Diametri tubo',
    es: 'Diámetros manguera',
    pt: 'Diâmetros mangueira',
    nl: 'Slangdiameters',
    pl: 'Średnice węży',
  },
  'hoseOptions.connectionDiameters': {
    en: 'Connection Diameters',
    de: 'Anschlussdurchmesser',
    fr: 'Diamètres raccord',
    it: 'Diametri raccordo',
    es: 'Diámetros conexión',
    pt: 'Diâmetros conexão',
    nl: 'Aansluitdiameters',
    pl: 'Średnice złączy',
  },
  'hoseOptions.hoseModels': {
    en: 'Hose Models',
    de: 'Schlauchmodelle',
    fr: 'Modèles de tuyaux',
    it: 'Modelli manichetta',
    es: 'Modelos de manguera',
    pt: 'Modelos de mangueira',
    nl: 'Slangmodellen',
    pl: 'Modele węży',
  },
  'hoseOptions.addValue': {
    en: 'Add',
    de: 'Hinzufügen',
    fr: 'Ajouter',
    it: 'Aggiungi',
    es: 'Añadir',
    pt: 'Adicionar',
    nl: 'Toevoegen',
    pl: 'Dodaj',
  },
  'hoseOptions.placeholder': {
    en: 'e.g. 25 mm',
    de: 'z.B. 25 mm',
    fr: 'p.ex. 25 mm',
    it: 'es. 25 mm',
    es: 'p.ej. 25 mm',
    pt: 'ex. 25 mm',
    nl: 'bijv. 25 mm',
    pl: 'np. 25 mm',
  },
  'hoseOptions.modelPlaceholder': {
    en: 'e.g. L30',
    de: 'z.B. L30',
    fr: 'p.ex. L30',
    it: 'es. L30',
    es: 'p.ej. L30',
    pt: 'ex. L30',
    nl: 'bijv. L30',
    pl: 'np. L30',
  },
  'hoseOptions.empty': {
    en: 'No entries yet. Add one above.',
    de: 'Noch keine Einträge. Oben hinzufügen.',
    fr: 'Aucune entrée. Ajoutez-en ci-dessus.',
    it: 'Nessuna voce. Aggiungine una sopra.',
    es: 'Sin entradas. Añade una arriba.',
    pt: 'Sem entradas. Adicione uma acima.',
    nl: 'Nog geen items. Voeg er een toe.',
    pl: 'Brak wpisów. Dodaj powyżej.',
  },

  // Overview (extra)
  'overview.customers': {
    en: 'customers',
    de: 'Kunden',
    fr: 'clients',
    it: 'clienti',
    es: 'clientes',
    pt: 'clientes',
    nl: 'klanten',
    pl: 'klientów',
  },
  'overview.export': {
    en: 'Export',
    de: 'Export',
    fr: 'Exporter',
    it: 'Esporta',
    es: 'Exportar',
    pt: 'Exportar',
    nl: 'Exporteer',
    pl: 'Eksport',
  },
  'overview.newCustomer': {
    en: 'New Customer',
    de: 'Neuer Kunde',
    fr: 'Nouveau client',
    it: 'Nuovo cliente',
    es: 'Nuevo cliente',
    pt: 'Novo cliente',
    nl: 'Nieuwe klant',
    pl: 'Nowy klient',
  },
  'overview.searchPlaceholder': {
    en: 'Search name, street, city, ID...',
    de: 'Name, Strasse, Ort, ID suchen...',
    fr: 'Rechercher nom, rue, ville, ID...',
    it: 'Cerca nome, via, città, ID...',
    es: 'Buscar nombre, calle, ciudad, ID...',
    pt: 'Pesquisar nome, rua, cidade, ID...',
    nl: 'Zoek naam, straat, stad, ID...',
    pl: 'Szukaj nazwy, ulicy, miasta, ID...',
  },
  'overview.allStatuses': {
    en: 'All statuses',
    de: 'Alle Status',
    fr: 'Tous les statuts',
    it: 'Tutti gli stati',
    es: 'Todos los estados',
    pt: 'Todos os estados',
    nl: 'Alle statussen',
    pl: 'Wszystkie statusy',
  },
  'overview.allWorkers': {
    en: 'All workers',
    de: 'Alle Mitarbeiter',
    fr: 'Tous les techniciens',
    it: 'Tutti i tecnici',
    es: 'Todos los técnicos',
    pt: 'Todos os técnicos',
    nl: 'Alle medewerkers',
    pl: 'Wszyscy pracownicy',
  },
  'overview.allCities': {
    en: 'All cities',
    de: 'Alle Orte',
    fr: 'Toutes les villes',
    it: 'Tutte le città',
    es: 'Todas las ciudades',
    pt: 'Todas as cidades',
    nl: 'Alle steden',
    pl: 'Wszystkie miasta',
  },
  'overview.company': {
    en: 'Company',
    de: 'Firma',
    fr: 'Société',
    it: 'Azienda',
    es: 'Empresa',
    pt: 'Empresa',
    nl: 'Bedrijf',
    pl: 'Firma',
  },
  'overview.nextInsp': {
    en: 'Next Insp.',
    de: 'Nächste Kontr.',
    fr: 'Proch. insp.',
    it: 'Pross. isp.',
    es: 'Próx. insp.',
    pt: 'Próx. insp.',
    nl: 'Volg. insp.',
    pl: 'Nast. kontr.',
  },

  // Workers
  'workers.title': {
    en: 'Workers',
    de: 'Mitarbeiter',
    fr: 'Techniciens',
    it: 'Tecnici',
    es: 'Técnicos',
    pt: 'Técnicos',
    nl: 'Medewerkers',
    pl: 'Pracownicy',
  },
  'workers.subtitle': {
    en: 'Service technicians',
    de: 'Servicetechniker',
    fr: 'Techniciens de service',
    it: 'Tecnici di servizio',
    es: 'Técnicos de servicio',
    pt: 'Técnicos de serviço',
    nl: 'Servicemonteurs',
    pl: 'Serwisanci',
  },
  'workers.newWorker': {
    en: 'New Worker',
    de: 'Neuer Mitarbeiter',
    fr: 'Nouveau technicien',
    it: 'Nuovo tecnico',
    es: 'Nuevo técnico',
    pt: 'Novo técnico',
    nl: 'Nieuwe medewerker',
    pl: 'Nowy pracownik',
  },
  'workers.team': {
    en: 'Team',
    de: 'Team',
    fr: 'Équipe',
    it: 'Team',
    es: 'Equipo',
    pt: 'Equipa',
    nl: 'Team',
    pl: 'Zespół',
  },
  'workers.active': {
    en: 'Active',
    de: 'Aktiv',
    fr: 'Actif',
    it: 'Attivo',
    es: 'Activo',
    pt: 'Ativo',
    nl: 'Actief',
    pl: 'Aktywny',
  },
  'workers.inactive': {
    en: 'Inactive',
    de: 'Inaktiv',
    fr: 'Inactif',
    it: 'Inattivo',
    es: 'Inactivo',
    pt: 'Inativo',
    nl: 'Inactief',
    pl: 'Nieaktywny',
  },

  // Spare parts
  'parts.title': {
    en: 'Spare Parts',
    de: 'Ersatzteile',
    fr: 'Pièces de rechange',
    it: 'Ricambi',
    es: 'Repuestos',
    pt: 'Peças sobressalentes',
    nl: 'Onderdelen',
    pl: 'Części zamienne',
  },
  'parts.subtitle': {
    en: 'Parts catalog with article numbers and pricing',
    de: 'Teilekatalog mit Artikelnummern und Preisen',
    fr: "Catalogue avec numéros d'articles et tarifs",
    it: 'Catalogo pezzi con numeri articolo e prezzi',
    es: 'Catálogo con números de artículo y precios',
    pt: 'Catálogo com números de artigo e preços',
    nl: 'Onderdelencatalogus met artikelnummers en prijzen',
    pl: 'Katalog z numerami artykułów i cenami',
  },
  'parts.newPart': {
    en: 'New Part',
    de: 'Neues Teil',
    fr: 'Nouvelle pièce',
    it: 'Nuovo ricambio',
    es: 'Nuevo repuesto',
    pt: 'Nova peça',
    nl: 'Nieuw onderdeel',
    pl: 'Nowa część',
  },
  'parts.extinguisher': {
    en: 'Extinguisher',
    de: 'Feuerlöscher',
    fr: 'Extincteur',
    it: 'Estintore',
    es: 'Extintor',
    pt: 'Extintor',
    nl: 'Brandblusser',
    pl: 'Gaśnica',
  },
  'parts.hose': {
    en: 'Hose',
    de: 'Löschposten',
    fr: 'Lance',
    it: 'Manichetta',
    es: 'Manguera',
    pt: 'Mangueira',
    nl: 'Slang',
    pl: 'Wąż',
  },
  'parts.searchPlaceholder': {
    en: 'Search article or name',
    de: 'Artikel oder Name suchen',
    fr: 'Rechercher article ou nom',
    it: 'Cerca articolo o nome',
    es: 'Buscar artículo o nombre',
    pt: 'Pesquisar artigo ou nome',
    nl: 'Zoek artikel of naam',
    pl: 'Szukaj artykułu lub nazwy',
  },

  // Manufacturers
  'mfr.title': {
    en: 'Manufacturers',
    de: 'Hersteller',
    fr: 'Fabricants',
    it: 'Produttori',
    es: 'Fabricantes',
    pt: 'Fabricantes',
    nl: 'Fabrikanten',
    pl: 'Producenci',
  },
  'mfr.subtitle': {
    en: 'Equipment brands and their model catalogs',
    de: 'Geräte-Marken und Modellkataloge',
    fr: 'Marques et catalogues de modèles',
    it: 'Marche e cataloghi modelli',
    es: 'Marcas y catálogos de modelos',
    pt: 'Marcas e catálogos de modelos',
    nl: 'Merken en modelcatalogi',
    pl: 'Marki i katalogi modeli',
  },
  'mfr.newMfr': {
    en: 'New Manufacturer',
    de: 'Neuer Hersteller',
    fr: 'Nouveau fabricant',
    it: 'Nuovo produttore',
    es: 'Nuevo fabricante',
    pt: 'Novo fabricante',
    nl: 'Nieuwe fabrikant',
    pl: 'Nowy producent',
  },
  'mfr.catalog': {
    en: 'Catalog',
    de: 'Katalog',
    fr: 'Catalogue',
    it: 'Catalogo',
    es: 'Catálogo',
    pt: 'Catálogo',
    nl: 'Catalogus',
    pl: 'Katalog',
  },
  'mfr.models': {
    en: 'models',
    de: 'Modelle',
    fr: 'modèles',
    it: 'modelli',
    es: 'modelos',
    pt: 'modelos',
    nl: 'modellen',
    pl: 'modeli',
  },
  'mfr.addModel': {
    en: 'Add Model',
    de: 'Modell hinzufügen',
    fr: 'Ajouter modèle',
    it: 'Aggiungi modello',
    es: 'Añadir modelo',
    pt: 'Adicionar modelo',
    nl: 'Model toevoegen',
    pl: 'Dodaj model',
  },

  // Device types
  'types.title': {
    en: 'Device Types',
    de: 'Gerätetypen',
    fr: "Types d'appareils",
    it: 'Tipi di dispositivi',
    es: 'Tipos de dispositivos',
    pt: 'Tipos de dispositivos',
    nl: 'Apparaattypen',
    pl: 'Typy urządzeń',
  },
  'types.subtitle': {
    en: 'Extinguisher classes, pressure + age-limit rules',
    de: 'Löscherklassen, Druck- und Altersgrenzen',
    fr: "Classes d'extincteurs, pression et limites d'âge",
    it: 'Classi estintori, pressione e limiti di età',
    es: 'Clases de extintores, presión y límites de edad',
    pt: 'Classes de extintores, pressão e limites de idade',
    nl: 'Blusserklassen, druk- en leeftijdsregels',
    pl: 'Klasy gaśnic, ciśnienie i limity wieku',
  },
  'types.newType': {
    en: 'New Type',
    de: 'Neuer Typ',
    fr: 'Nouveau type',
    it: 'Nuovo tipo',
    es: 'Nuevo tipo',
    pt: 'Novo tipo',
    nl: 'Nieuw type',
    pl: 'Nowy typ',
  },
  'types.years': {
    en: 'y',
    de: 'J',
    fr: 'a',
    it: 'a',
    es: 'a',
    pt: 'a',
    nl: 'j',
    pl: 'l',
  },

  // Service prices
  'prices.title': {
    en: 'Service Prices',
    de: 'Servicepreise',
    fr: 'Tarifs',
    it: 'Prezzi servizio',
    es: 'Precios de servicio',
    pt: 'Preços de serviço',
    nl: 'Serviceprijzen',
    pl: 'Ceny usług',
  },
  'prices.subtitle': {
    en: 'Maintenance pricing per device type',
    de: 'Wartungspreise pro Gerätetyp',
    fr: "Tarifs de maintenance par type d'appareil",
    it: 'Prezzi manutenzione per tipo dispositivo',
    es: 'Precios de mantenimiento por tipo',
    pt: 'Preços de manutenção por tipo',
    nl: 'Onderhoudsprijzen per apparaattype',
    pl: 'Ceny konserwacji wg typu urządzenia',
  },
  'prices.newPrice': {
    en: 'New Price',
    de: 'Neuer Preis',
    fr: 'Nouveau tarif',
    it: 'Nuovo prezzo',
    es: 'Nuevo precio',
    pt: 'Novo preço',
    nl: 'Nieuwe prijs',
    pl: 'Nowa cena',
  },
  'prices.priceList': {
    en: 'Price List',
    de: 'Preisliste',
    fr: 'Liste de prix',
    it: 'Listino prezzi',
    es: 'Lista de precios',
    pt: 'Lista de preços',
    nl: 'Prijslijst',
    pl: 'Cennik',
  },

  // Reports
  'reports.sparePartsSummary': {
    en: 'Spare Parts Summary',
    de: 'Ersatzteilübersicht',
    fr: 'Résumé pièces de rechange',
    it: 'Riepilogo ricambi',
    es: 'Resumen de repuestos',
    pt: 'Resumo de peças',
    nl: 'Overzicht onderdelen',
    pl: 'Podsumowanie części',
  },
  'reports.noPartsUsed': {
    en: 'No parts used',
    de: 'Keine Teile verwendet',
    fr: 'Aucune pièce utilisée',
    it: 'Nessun ricambio usato',
    es: 'Sin repuestos usados',
    pt: 'Sem peças usadas',
    nl: 'Geen onderdelen gebruikt',
    pl: 'Brak użytych części',
  },
  'reports.upcomingInspections': {
    en: 'Upcoming Inspections (90d)',
    de: 'Anstehende Kontrollen (90T)',
    fr: 'Inspections à venir (90j)',
    it: 'Ispezioni imminenti (90g)',
    es: 'Inspecciones próximas (90d)',
    pt: 'Inspeções próximas (90d)',
    nl: 'Komende inspecties (90d)',
    pl: 'Nadchodzące kontrole (90d)',
  },
  'reports.none': {
    en: 'None',
    de: 'Keine',
    fr: 'Aucun',
    it: 'Nessuno',
    es: 'Ninguno',
    pt: 'Nenhum',
    nl: 'Geen',
    pl: 'Brak',
  },
  'reports.ageWarnings': {
    en: 'Age Warnings',
    de: 'Alterswarnungen',
    fr: "Avertissements d'âge",
    it: 'Avvisi di età',
    es: 'Avisos de edad',
    pt: 'Avisos de idade',
    nl: 'Leeftijdswaarschuwingen',
    pl: 'Ostrzeżenia wiekowe',
  },
  'reports.exceedsAgeLimit': {
    en: 'exceeds',
    de: 'überschreitet',
    fr: 'dépasse',
    it: 'supera',
    es: 'supera',
    pt: 'excede',
    nl: 'overschrijdt',
    pl: 'przekracza',
  },
  'reports.pressureTestDue': {
    en: 'Pressure Test Due (5y)',
    de: 'Druckprüfung fällig (5J)',
    fr: 'Test pression dû (5a)',
    it: 'Test pressione scaduto (5a)',
    es: 'Prueba presión pendiente (5a)',
    pt: 'Teste pressão pendente (5a)',
    nl: 'Druktest verschuldigd (5j)',
    pl: 'Test ciśnieniowy (5l)',
  },
  'reports.pressureTestSpec': {
    en: '12 Bar · EN 671-3',
    de: '12 Bar · SN EN 671-3',
    fr: '12 Bar · NF EN 671-3',
    it: '12 Bar · UNI EN 671-3',
    es: '12 Bar · UNE EN 671-3',
    pt: '12 Bar · NP EN 671-3',
    nl: '12 Bar · NEN EN 671-3',
    pl: '12 Bar · PN EN 671-3',
  },
  'reports.due': {
    en: 'due',
    de: 'fällig',
    fr: 'à faire',
    it: 'scaduti',
    es: 'pendientes',
    pt: 'pendentes',
    nl: 'verschuldigd',
    pl: 'do wykonania',
  },

  // Hose report
  'hoseReport.title': {
    en: 'Hose Service Report',
    de: 'Löschposten Servicebericht',
    fr: 'Rapport service lances',
    it: 'Rapporto servizio manichette',
    es: 'Informe servicio mangueras',
    pt: 'Relatório serviço mangueiras',
    nl: 'Slang servicerapport',
    pl: 'Raport serwisu węży',
  },
  'hoseReport.date': {
    en: 'Date',
    de: 'Datum',
    fr: 'Date',
    it: 'Data',
    es: 'Fecha',
    pt: 'Data',
    nl: 'Datum',
    pl: 'Data',
  },
  'hoseReport.customer': {
    en: 'Customer',
    de: 'Kunde',
    fr: 'Client',
    it: 'Cliente',
    es: 'Cliente',
    pt: 'Cliente',
    nl: 'Klant',
    pl: 'Klient',
  },
  'hoseReport.objectSite': {
    en: 'Object / Site',
    de: 'Objekt / Standort',
    fr: 'Objet / Site',
    it: 'Oggetto / Sito',
    es: 'Objeto / Sitio',
    pt: 'Objeto / Local',
    nl: 'Object / Locatie',
    pl: 'Obiekt / Lokalizacja',
  },
  'hoseReport.standard': {
    en: 'Standard: Annual inspection per SN EN 671-3. 12 bar pressure test every 5 years.',
    de: 'Standard: Jährliche Kontrolle nach SN EN 671-3. 12 bar Druckprüfung alle 5 Jahre.',
    fr: 'Norme : inspection annuelle SN EN 671-3. Test pression 12 bar tous les 5 ans.',
    it: 'Standard: ispezione annuale SN EN 671-3. Test pressione 12 bar ogni 5 anni.',
    es: 'Norma: inspección anual SN EN 671-3. Prueba presión 12 bar cada 5 años.',
    pt: 'Norma: inspeção anual SN EN 671-3. Teste pressão 12 bar a cada 5 anos.',
    nl: 'Norm: jaarlijkse inspectie SN EN 671-3. 12 bar druktest elke 5 jaar.',
    pl: 'Norma: roczna kontrola SN EN 671-3. Test ciśnieniowy 12 bar co 5 lat.',
  },
  'hoseReport.equipment': {
    en: 'Equipment',
    de: 'Ausrüstung',
    fr: 'Équipement',
    it: 'Attrezzatura',
    es: 'Equipo',
    pt: 'Equipamento',
    nl: 'Apparatuur',
    pl: 'Sprzęt',
  },
  'hoseReport.condition': {
    en: 'Condition',
    de: 'Zustand',
    fr: 'État',
    it: 'Condizione',
    es: 'Condición',
    pt: 'Condição',
    nl: 'Conditie',
    pl: 'Stan',
  },
  'hoseReport.measurements': {
    en: 'Measurements',
    de: 'Messwerte',
    fr: 'Mesures',
    it: 'Misurazioni',
    es: 'Mediciones',
    pt: 'Medições',
    nl: 'Metingen',
    pl: 'Pomiary',
  },
  'hoseReport.year': {
    en: 'Year',
    de: 'Baujahr',
    fr: 'Année',
    it: 'Anno',
    es: 'Año',
    pt: 'Ano',
    nl: 'Jaar',
    pl: 'Rok',
  },
  'hoseReport.length': {
    en: 'Length',
    de: 'Länge',
    fr: 'Longueur',
    it: 'Lunghezza',
    es: 'Longitud',
    pt: 'Comprimento',
    nl: 'Lengte',
    pl: 'Długość',
  },
  'hoseReport.connection': {
    en: 'Connection',
    de: 'Anschluss',
    fr: 'Raccord',
    it: 'Connessione',
    es: 'Conexión',
    pt: 'Conexão',
    nl: 'Aansluiting',
    pl: 'Przyłącze',
  },
  'hoseReport.resting': {
    en: 'Resting pressure',
    de: 'Ruhedruck',
    fr: 'Pression repos',
    it: 'Pressione riposo',
    es: 'Presión reposo',
    pt: 'Pressão repouso',
    nl: 'Rustdruk',
    pl: 'Ciśnienie spoczynkowe',
  },
  'hoseReport.flow': {
    en: 'Flow pressure',
    de: 'Fliessdruck',
    fr: 'Pression débit',
    it: 'Pressione flusso',
    es: 'Presión flujo',
    pt: 'Pressão fluxo',
    nl: 'Stroomdruk',
    pl: 'Ciśnienie przepływu',
  },
  'hoseReport.flowRate': {
    en: 'Flow rate',
    de: 'Durchfluss',
    fr: 'Débit',
    it: 'Portata',
    es: 'Caudal',
    pt: 'Vazão',
    nl: 'Debiet',
    pl: 'Przepływ',
  },
  'hoseReport.pressureTest': {
    en: '12-bar test',
    de: '12-Bar-Prüfung',
    fr: 'Test 12 bar',
    it: 'Test 12 bar',
    es: 'Prueba 12 bar',
    pt: 'Teste 12 bar',
    nl: '12-bar test',
    pl: 'Test 12 bar',
  },
  'hoseReport.techSignature': {
    en: 'Technician signature',
    de: 'Unterschrift Techniker',
    fr: 'Signature technicien',
    it: 'Firma tecnico',
    es: 'Firma técnico',
    pt: 'Assinatura técnico',
    nl: 'Handtekening technicus',
    pl: 'Podpis technika',
  },
  'hoseReport.custSignature': {
    en: 'Customer signature',
    de: 'Unterschrift Kunde',
    fr: 'Signature client',
    it: 'Firma cliente',
    es: 'Firma cliente',
    pt: 'Assinatura cliente',
    nl: 'Handtekening klant',
    pl: 'Podpis klienta',
  },
  'hoseReport.noHoses': {
    en: 'No hoses recorded',
    de: 'Keine Löschposten erfasst',
    fr: 'Aucune lance enregistrée',
    it: 'Nessuna manichetta registrata',
    es: 'Sin mangueras registradas',
    pt: 'Nenhuma mangueira registrada',
    nl: 'Geen slangen geregistreerd',
    pl: 'Brak zarejestrowanych węży',
  },
  'hoseReport.conditionFireCock': {
    en: 'Fire cock',
    de: 'Feuerhahn',
    fr: 'Robinet incendie',
    it: 'Rubinetto antincendio',
    es: 'Llave de incendio',
    pt: 'Torneira incêndio',
    nl: 'Brandkraan',
    pl: 'Zawór pożarowy',
  },
  'hoseReport.conditionSeals': {
    en: 'Seals',
    de: 'Dichtungen',
    fr: 'Joints',
    it: 'Guarnizioni',
    es: 'Juntas',
    pt: 'Vedações',
    nl: 'Afdichtingen',
    pl: 'Uszczelki',
  },
  'hoseReport.conditionHose': {
    en: 'Hose',
    de: 'Schlauch',
    fr: 'Tuyau',
    it: 'Manichetta',
    es: 'Manguera',
    pt: 'Mangueira',
    nl: 'Slang',
    pl: 'Wąż',
  },
  'hoseReport.conditionNozzle': {
    en: 'Nozzle',
    de: 'Düse',
    fr: 'Buse',
    it: 'Ugello',
    es: 'Boquilla',
    pt: 'Bocal',
    nl: 'Mondstuk',
    pl: 'Dysza',
  },
  'hoseReport.conditionAxle': {
    en: 'Axle',
    de: 'Achse',
    fr: 'Axe',
    it: 'Asse',
    es: 'Eje',
    pt: 'Eixo',
    nl: 'As',
    pl: 'Oś',
  },
  'hoseReport.conditionSwivelArm': {
    en: 'Swivel arm',
    de: 'Schwenkarm',
    fr: 'Bras pivotant',
    it: 'Braccio girevole',
    es: 'Brazo giratorio',
    pt: 'Braço giratório',
    nl: 'Zwenkarm',
    pl: 'Ramię obrotowe',
  },
  'hoseReport.conditionDoorLock': {
    en: 'Door lock',
    de: 'Türschloss',
    fr: 'Serrure',
    it: 'Serratura',
    es: 'Cerradura',
    pt: 'Fechadura',
    nl: 'Deurslot',
    pl: 'Zamek drzwi',
  },
  'hoseReport.conditionWater': {
    en: 'Water',
    de: 'Wasser',
    fr: 'Eau',
    it: 'Acqua',
    es: 'Agua',
    pt: 'Água',
    nl: 'Water',
    pl: 'Woda',
  },

  // Map
  'map.title': {
    en: 'Map',
    de: 'Karte',
    fr: 'Carte',
    it: 'Mappa',
    es: 'Mapa',
    pt: 'Mapa',
    nl: 'Kaart',
    pl: 'Mapa',
  },

  // Import/Export
  'io.title': {
    en: 'Import / Export',
    de: 'Import / Export',
    fr: 'Import / Export',
    it: 'Import / Export',
    es: 'Importar / Exportar',
    pt: 'Importar / Exportar',
    nl: 'Import / Export',
    pl: 'Import / Eksport',
  },
  'io.subtitle': {
    en: 'Backup, restore, or migrate from Excel',
    de: 'Sichern, Wiederherstellen oder aus Excel migrieren',
    fr: 'Sauvegarde, restauration ou migration depuis Excel',
    it: 'Backup, ripristino o migrazione da Excel',
    es: 'Copia de seguridad, restauración o migración desde Excel',
    pt: 'Backup, restauração ou migração do Excel',
    nl: 'Back-up, herstel of migratie vanuit Excel',
    pl: 'Kopia zapasowa, przywracanie lub migracja z Excela',
  },
  'io.downloadJson': {
    en: 'Download JSON',
    de: 'JSON herunterladen',
    fr: 'Télécharger JSON',
    it: 'Scarica JSON',
    es: 'Descargar JSON',
    pt: 'Baixar JSON',
    nl: 'JSON downloaden',
    pl: 'Pobierz JSON',
  },
  'io.importJson': {
    en: 'Import JSON',
    de: 'JSON importieren',
    fr: 'Importer JSON',
    it: 'Importa JSON',
    es: 'Importar JSON',
    pt: 'Importar JSON',
    nl: 'JSON importeren',
    pl: 'Importuj JSON',
  },
  'io.dropped': {
    en: 'Dropped',
    de: 'Verworfen',
    fr: 'Rejeté',
    it: 'Scartato',
    es: 'Descartado',
    pt: 'Descartado',
    nl: 'Verworpen',
    pl: 'Odrzucono',
  },

  // Sidebar
  'sidebar.systemReady': {
    en: 'System ready',
    de: 'System bereit',
    fr: 'Système prêt',
    it: 'Sistema pronto',
    es: 'Sistema listo',
    pt: 'Sistema pronto',
    nl: 'Systeem gereed',
    pl: 'System gotowy',
  },
  'sidebar.offlineMode': {
    en: 'Offline mode',
    de: 'Offline-Modus',
    fr: 'Mode hors ligne',
    it: 'Modalità offline',
    es: 'Modo sin conexión',
    pt: 'Modo offline',
    nl: 'Offline modus',
    pl: 'Tryb offline',
  },

  'sidebar.checkUpdate': {
    en: 'Check for updates',
    de: 'Nach Updates suchen',
    fr: 'Vérifier les mises à jour',
    it: 'Controlla aggiornamenti',
    es: 'Buscar actualizaciones',
    pt: 'Verificar atualizações',
    nl: 'Controleer op updates',
    pl: 'Sprawdź aktualizacje',
  },
  'sidebar.checking': {
    en: 'Checking…',
    de: 'Prüfe…',
    fr: 'Vérification…',
    it: 'Controllo…',
    es: 'Comprobando…',
    pt: 'Verificando…',
    nl: 'Controleren…',
    pl: 'Sprawdzanie…',
  },
  'sidebar.upToDate': {
    en: 'Up to date',
    de: 'Aktuell',
    fr: 'À jour',
    it: 'Aggiornato',
    es: 'Actualizado',
    pt: 'Atualizado',
    nl: 'Up-to-date',
    pl: 'Aktualne',
  },
  'sidebar.updating': {
    en: 'Syncing update…',
    de: 'Update wird geladen…',
    fr: 'Mise à jour en cours…',
    it: 'Aggiornamento in corso…',
    es: 'Sincronizando actualización…',
    pt: 'Sincronizando atualização…',
    nl: 'Update synchroniseren…',
    pl: 'Synchronizacja aktualizacji…',
  },
  'sidebar.caching': {
    en: 'Caching for offline…',
    de: 'Wird offline verfügbar…',
    fr: 'Mise en cache…',
    it: 'Memorizzazione offline…',
    es: 'Guardando sin conexión…',
    pt: 'Armazenando offline…',
    nl: 'Offline opslaan…',
    pl: 'Zapisywanie offline…',
  },
  'sidebar.offlineReady': {
    en: 'Ready for offline',
    de: 'Offline bereit',
    fr: 'Prêt hors ligne',
    it: 'Pronto offline',
    es: 'Listo sin conexión',
    pt: 'Pronto offline',
    nl: 'Offline gereed',
    pl: 'Gotowe offline',
  },

  // Install prompt
  'install.button': {
    en: 'Install App',
    de: 'App installieren',
    fr: "Installer l'appli",
    it: 'Installa app',
    es: 'Instalar app',
    pt: 'Instalar app',
    nl: 'App installeren',
    pl: 'Zainstaluj',
  },
  'install.dismiss': {
    en: 'Not now',
    de: 'Nicht jetzt',
    fr: 'Pas maintenant',
    it: 'Non ora',
    es: 'Ahora no',
    pt: 'Agora não',
    nl: 'Niet nu',
    pl: 'Nie teraz',
  },
  'install.ios.title': {
    en: 'Add to Home Screen',
    de: 'Zum Startbildschirm',
    fr: "Ajouter à l'écran d'accueil",
    it: 'Aggiungi a Home',
    es: 'Añadir a inicio',
    pt: 'Adicionar ao ecrã',
    nl: 'Voeg toe aan beginscherm',
    pl: 'Dodaj do ekranu',
  },
  'install.ios.step1': {
    en: 'Tap the Share button',
    de: 'Tippe auf Teilen',
    fr: 'Appuyez sur Partager',
    it: 'Tocca Condividi',
    es: 'Pulsa Compartir',
    pt: 'Toque em Partilhar',
    nl: 'Tik op Deel',
    pl: 'Kliknij Udostępnij',
  },
  'install.ios.step2': {
    en: 'Select "Add to Home Screen"',
    de: '„Zum Home-Bildschirm" wählen',
    fr: "Choisir « Sur l'écran d'accueil »",
    it: 'Seleziona "Aggiungi a Home"',
    es: 'Selecciona "Añadir a inicio"',
    pt: 'Selecione "Adicionar ao ecrã inicial"',
    nl: 'Kies "Zet op beginscherm"',
    pl: 'Wybierz "Dodaj do ekranu głównego"',
  },
  'install.chrome.title': {
    en: 'Install App',
    de: 'App installieren',
    fr: "Installer l'appli",
    it: 'Installa app',
    es: 'Instalar app',
    pt: 'Instalar app',
    nl: 'App installeren',
    pl: 'Zainstaluj',
  },
  'install.chrome.step1': {
    en: 'Open browser menu (\u22EE)',
    de: 'Browsermen\u00FC \u00F6ffnen (\u22EE)',
    fr: 'Ouvrir le menu du navigateur (\u22EE)',
    it: 'Apri il menu del browser (\u22EE)',
    es: 'Abrir men\u00FA del navegador (\u22EE)',
    pt: 'Abrir menu do navegador (\u22EE)',
    nl: 'Open browsermenu (\u22EE)',
    pl: 'Otw\u00F3rz menu przegl\u0105darki (\u22EE)',
  },
  'install.chrome.step2': {
    en: 'Select "Install Fire Track"',
    de: '\u201EFire Track installieren\u201C w\u00E4hlen',
    fr: 'Choisir \u00AB Installer Fire Track \u00BB',
    it: 'Seleziona "Installa Fire Track"',
    es: 'Selecciona "Instalar Fire Track"',
    pt: 'Selecione "Instalar Fire Track"',
    nl: 'Kies "Fire Track installeren"',
    pl: 'Wybierz "Zainstaluj Fire Track"',
  },
  'install.safari.title': {
    en: 'Add to Dock',
    de: 'Zum Dock hinzuf\u00FCgen',
    fr: 'Ajouter au Dock',
    it: 'Aggiungi al Dock',
    es: 'A\u00F1adir al Dock',
    pt: 'Adicionar ao Dock',
    nl: 'Voeg toe aan Dock',
    pl: 'Dodaj do Docka',
  },
  'install.safari.step1': {
    en: 'Click File in the menu bar',
    de: 'Klicke auf Ablage in der Men\u00FCleiste',
    fr: 'Cliquez sur Fichier dans la barre de menus',
    it: 'Clicca su File nella barra dei menu',
    es: 'Haz clic en Archivo en la barra de men\u00FAs',
    pt: 'Clique em Ficheiro na barra de menus',
    nl: 'Klik op Archief in de menubalk',
    pl: 'Kliknij Plik na pasku menu',
  },
  'install.safari.step2': {
    en: 'Select "Add to Dock"',
    de: '\u201EZum Dock hinzuf\u00FCgen\u201C w\u00E4hlen',
    fr: 'Choisir \u00AB Ajouter au Dock \u00BB',
    it: 'Seleziona "Aggiungi al Dock"',
    es: 'Selecciona "A\u00F1adir al Dock"',
    pt: 'Selecione "Adicionar ao Dock"',
    nl: 'Kies "Voeg toe aan Dock"',
    pl: 'Wybierz "Dodaj do Docka"',
  },
  'install.firefox': {
    en: "Firefox doesn't support app install. Use Chrome, Edge, or Safari.",
    de: 'Firefox unterstützt keine App-Installation. Nutze Chrome, Edge oder Safari.',
    fr: "Firefox ne prend pas en charge l'installation. Utilisez Chrome, Edge ou Safari.",
    it: "Firefox non supporta l'installazione. Usa Chrome, Edge o Safari.",
    es: 'Firefox no permite instalar. Usa Chrome, Edge o Safari.',
    pt: 'Firefox não suporta instalação. Use Chrome, Edge ou Safari.',
    nl: 'Firefox ondersteunt geen installatie. Gebruik Chrome, Edge of Safari.',
    pl: 'Firefox nie obsługuje instalacji. Użyj Chrome, Edge lub Safari.',
  },

  // Settings (extra labels)
  'settings.companyName': {
    en: 'Company Name',
    de: 'Firmenname',
    fr: 'Nom de la société',
    it: 'Nome azienda',
    es: 'Nombre de empresa',
    pt: 'Nome da empresa',
    nl: 'Bedrijfsnaam',
    pl: 'Nazwa firmy',
  },
  'settings.street': {
    en: 'Street',
    de: 'Strasse',
    fr: 'Rue',
    it: 'Via',
    es: 'Calle',
    pt: 'Rua',
    nl: 'Straat',
    pl: 'Ulica',
  },
  'settings.zip': {
    en: 'ZIP',
    de: 'PLZ',
    fr: 'Code postal',
    it: 'CAP',
    es: 'C.P.',
    pt: 'CEP',
    nl: 'Postcode',
    pl: 'Kod pocztowy',
  },
  'settings.city': {
    en: 'City',
    de: 'Ort',
    fr: 'Ville',
    it: 'Città',
    es: 'Ciudad',
    pt: 'Cidade',
    nl: 'Stad',
    pl: 'Miasto',
  },
  'settings.phone': {
    en: 'Phone',
    de: 'Telefon',
    fr: 'Téléphone',
    it: 'Telefono',
    es: 'Teléfono',
    pt: 'Telefone',
    nl: 'Telefoon',
    pl: 'Telefon',
  },
  'settings.email': {
    en: 'Email',
    de: 'E-Mail',
    fr: 'E-mail',
    it: 'Email',
    es: 'Correo',
    pt: 'E-mail',
    nl: 'E-mail',
    pl: 'E-mail',
  },
  'settings.currency': {
    en: 'Currency',
    de: 'Währung',
    fr: 'Devise',
    it: 'Valuta',
    es: 'Moneda',
    pt: 'Moeda',
    nl: 'Valuta',
    pl: 'Waluta',
  },
  'settings.rounding': {
    en: 'Rounding',
    de: 'Rundung',
    fr: 'Arrondi',
    it: 'Arrotondamento',
    es: 'Redondeo',
    pt: 'Arredondamento',
    nl: 'Afronding',
    pl: 'Zaokrąglenie',
  },
  'settings.currencyHint': {
    en: 'Auto-set by country',
    de: 'Automatisch nach Land',
    fr: 'Défini automatiquement',
    it: 'Impostato automaticamente',
    es: 'Se establece automáticamente',
    pt: 'Definido automaticamente',
    nl: 'Automatisch ingesteld',
    pl: 'Ustawiane automatycznie',
  },
  'settings.iban': {
    en: 'IBAN',
    de: 'IBAN',
    fr: 'IBAN',
    it: 'IBAN',
    es: 'IBAN',
    pt: 'IBAN',
    nl: 'IBAN',
    pl: 'IBAN',
  },
  'settings.paymentTermsDays': {
    en: 'Payment Terms (days)',
    de: 'Zahlungsfrist (Tage)',
    fr: 'Délai de paiement (jours)',
    it: 'Termini di pagamento (giorni)',
    es: 'Plazo de pago (días)',
    pt: 'Prazo de pagamento (dias)',
    nl: 'Betalingstermijn (dagen)',
    pl: 'Termin płatności (dni)',
  },
  'customer.batchInspect': {
    en: 'Mark All Inspected',
    de: 'Alle als kontrolliert',
    fr: 'Tout inspecté',
    it: 'Tutti ispezionati',
    es: 'Marcar todos inspeccionados',
    pt: 'Marcar todos inspecionados',
    nl: 'Alles geïnspecteerd',
    pl: 'Wszystkie skontrolowane',
  },
  'customer.batchInspectDone': {
    en: 'All inspection dates updated',
    de: 'Alle Kontrolldaten aktualisiert',
    fr: "Dates d'inspection mises à jour",
    it: 'Date ispezione aggiornate',
    es: 'Fechas de inspección actualizadas',
    pt: 'Datas de inspeção atualizadas',
    nl: 'Inspectiedatums bijgewerkt',
    pl: 'Daty kontroli zaktualizowane',
  },
  'settings.travelSurcharge': {
    en: 'Travel Surcharge',
    de: 'Anfahrtspauschale',
    fr: 'Forfait déplacement',
    it: 'Supplemento viaggio',
    es: 'Recargo desplazamiento',
    pt: 'Taxa deslocação',
    nl: 'Reistoeslag',
    pl: 'Ryczałt dojazd',
  },
  'settings.inspectionInterval': {
    en: 'Inspection Interval (y)',
    de: 'Kontrollintervall (J)',
    fr: "Intervalle d'inspection (a)",
    it: 'Intervallo ispezione (a)',
    es: 'Intervalo de inspección (a)',
    pt: 'Intervalo de inspeção (a)',
    nl: 'Inspectie-interval (j)',
    pl: 'Interwał kontroli (l)',
  },
  'settings.boostAgeLimit': {
    en: 'Boost Age Limit (y)',
    de: 'Auflade-Altersgrenze (J)',
    fr: "Limite d'âge pression (a)",
    it: 'Limite età boost (a)',
    es: 'Límite edad boost (a)',
    pt: 'Limite idade boost (a)',
    nl: 'Opdruk leeftijdslimiet (j)',
    pl: 'Limit wieku boost (l)',
  },
  'settings.permanentAgeLimit': {
    en: 'Permanent Age Limit (y)',
    de: 'Dauerdr.-Altersgrenze (J)',
    fr: "Limite d'âge permanent (a)",
    it: 'Limite età permanente (a)',
    es: 'Límite edad permanente (a)',
    pt: 'Limite idade permanente (a)',
    nl: 'Permanent leeftijdslimiet (j)',
    pl: 'Limit wieku stałe (l)',
  },
  'settings.hoseTestInterval': {
    en: 'Hose Pressure Test (y)',
    de: 'Schlauch-Druckprüfung (J)',
    fr: 'Test pression tuyau (a)',
    it: 'Test pressione tubo (a)',
    es: 'Prueba presión manguera (a)',
    pt: 'Teste pressão mangueira (a)',
    nl: 'Slang druktest (j)',
    pl: 'Test ciśnieniowy węża (l)',
  },

  // Tour
  'tour.next': {
    en: 'Next',
    de: 'Weiter',
    fr: 'Suivant',
    it: 'Avanti',
    es: 'Siguiente',
    pt: 'Próximo',
    nl: 'Volgende',
    pl: 'Dalej',
  },
  'tour.prev': {
    en: 'Back',
    de: 'Zurück',
    fr: 'Précédent',
    it: 'Indietro',
    es: 'Anterior',
    pt: 'Anterior',
    nl: 'Vorige',
    pl: 'Wstecz',
  },
  'tour.skip': {
    en: 'Skip',
    de: 'Überspringen',
    fr: 'Passer',
    it: 'Salta',
    es: 'Omitir',
    pt: 'Pular',
    nl: 'Overslaan',
    pl: 'Pomiń',
  },
  'tour.finish': {
    en: 'Finish',
    de: 'Fertig',
    fr: 'Terminer',
    it: 'Fine',
    es: 'Finalizar',
    pt: 'Concluir',
    nl: 'Voltooien',
    pl: 'Zakończ',
  },
  'tour.step': {
    en: 'Step',
    de: 'Schritt',
    fr: 'Étape',
    it: 'Passo',
    es: 'Paso',
    pt: 'Etapa',
    nl: 'Stap',
    pl: 'Krok',
  },

  // ── Tour steps ──
  'tour.welcome.title': {
    en: 'Welcome to Fire Track',
    de: 'Willkommen bei Fire Track',
    fr: 'Bienvenue sur Fire Track',
    it: 'Benvenuto su Fire Track',
    es: 'Bienvenido a Fire Track',
    pt: 'Bem-vindo ao Fire Track',
    nl: 'Welkom bij Fire Track',
    pl: 'Witamy w Fire Track',
  },
  'tour.welcome.body': {
    en: 'A modern replacement for your XLSB workflow — customer management, inspections, invoicing and offline field work, all in one PWA.',
    de: 'Moderner Ersatz für Ihre XLSB — Kundenverwaltung, Kontrollen, Rechnungen und Offline-Feldarbeit in einer PWA.',
    fr: 'Remplacement moderne de votre flux XLSB — gestion clients, inspections, facturation et travail terrain hors ligne, le tout dans une PWA.',
    it: 'Sostituzione moderna del flusso XLSB — gestione clienti, ispezioni, fatturazione e lavoro sul campo offline, tutto in una PWA.',
    es: 'Reemplazo moderno de su flujo XLSB — gestión de clientes, inspecciones, facturación y trabajo de campo sin conexión, todo en una PWA.',
    pt: 'Substituto moderno do seu fluxo XLSB — gestão de clientes, inspeções, faturação e trabalho de campo offline, tudo numa PWA.',
    nl: 'Moderne vervanging voor uw XLSB-werkstroom — klantbeheer, inspecties, facturering en offline veldwerk, alles in één PWA.',
    pl: 'Nowoczesny zamiennik przepływu XLSB — zarządzanie klientami, inspekcje, fakturowanie i praca w terenie offline, wszystko w jednej PWA.',
  },
  'tour.nav.title': {
    en: 'Navigation',
    de: 'Navigation',
    fr: 'Navigation',
    it: 'Navigazione',
    es: 'Navegación',
    pt: 'Navegação',
    nl: 'Navigatie',
    pl: 'Nawigacja',
  },
  'tour.nav.body': {
    en: 'Main groups: Overview & Map, Administration master data, and Data import/export. Badges show live counts.',
    de: 'Hauptgruppen: Übersicht & Karte, Verwaltung, Daten Import/Export. Badges zeigen aktuelle Anzahl.',
    fr: 'Groupes principaux : Aperçu & Carte, Administration, Import/Export. Les badges affichent les compteurs en direct.',
    it: 'Gruppi principali: Panoramica & Mappa, Amministrazione, Import/Export. I badge mostrano i conteggi in tempo reale.',
    es: 'Grupos principales: Resumen y Mapa, Administración, Importar/Exportar. Las insignias muestran conteos en vivo.',
    pt: 'Grupos principais: Visão geral e Mapa, Administração, Importar/Exportar. Os emblemas mostram contagens em tempo real.',
    nl: 'Hoofdgroepen: Overzicht & Kaart, Administratie, Gegevens import/export. Badges tonen live aantallen.',
    pl: 'Główne grupy: Przegląd i Mapa, Administracja, Import/Eksport danych. Odznaki pokazują bieżące liczby.',
  },
  'tour.overview.title': {
    en: 'Customer Overview',
    de: 'Kundenübersicht',
    fr: 'Aperçu des clients',
    it: 'Panoramica clienti',
    es: 'Resumen de clientes',
    pt: 'Visão geral dos clientes',
    nl: 'Klantenoverzicht',
    pl: 'Przegląd klientów',
  },
  'tour.overview.body': {
    en: 'All sites at a glance. Status dots: green = done, blue = to do, amber = waiting. Click a row to open the customer.',
    de: 'Alle Standorte auf einen Blick. Statuspunkte: grün = erledigt, blau = offen, gelb = wartend. Zeile klicken öffnet Kunden.',
    fr: "Tous les sites en un coup d'œil. Points d'état : vert = terminé, bleu = à faire, orange = en attente. Cliquez sur une ligne pour ouvrir le client.",
    it: "Tutti i siti a colpo d'occhio. Punti di stato: verde = fatto, blu = da fare, arancione = in attesa. Clicca su una riga per aprire il cliente.",
    es: 'Todos los sitios de un vistazo. Puntos de estado: verde = hecho, azul = pendiente, ámbar = en espera. Haga clic en una fila para abrir el cliente.',
    pt: 'Todos os locais de relance. Pontos de estado: verde = concluído, azul = a fazer, âmbar = em espera. Clique numa linha para abrir o cliente.',
    nl: 'Alle locaties in één oogopslag. Statuspunten: groen = klaar, blauw = te doen, amber = wachtend. Klik op een rij om de klant te openen.',
    pl: 'Wszystkie lokalizacje na pierwszy rzut oka. Kropki statusu: zielona = gotowe, niebieska = do zrobienia, pomarańczowa = oczekujące. Kliknij wiersz, aby otworzyć klienta.',
  },
  'tour.map.title': {
    en: 'Map View',
    de: 'Kartenansicht',
    fr: 'Vue carte',
    it: 'Vista mappa',
    es: 'Vista de mapa',
    pt: 'Vista do mapa',
    nl: 'Kaartweergave',
    pl: 'Widok mapy',
  },
  'tour.map.body': {
    en: 'Every customer plotted with status-colored markers. Geocoded automatically when you save an object address.',
    de: 'Alle Kunden mit statusfarbigen Markern. Automatisch geokodiert beim Speichern der Objektadresse.',
    fr: "Chaque client affiché avec des marqueurs colorés par statut. Géocodage automatique lors de l'enregistrement de l'adresse.",
    it: "Ogni cliente tracciato con marcatori colorati per stato. Geocodifica automatica al salvataggio dell'indirizzo.",
    es: 'Cada cliente trazado con marcadores coloreados por estado. Geocodificación automática al guardar la dirección.',
    pt: 'Cada cliente marcado com marcadores coloridos por estado. Geocodificação automática ao guardar o endereço.',
    nl: 'Elke klant geplot met statusgekleurde markers. Automatisch gegeocodeerd bij het opslaan van het adres.',
    pl: 'Każdy klient zaznaczony kolorowymi markerami statusu. Automatyczna geokodyfikacja przy zapisie adresu.',
  },
  'tour.reports.title': {
    en: 'Reports',
    de: 'Rapporte',
    fr: 'Rapports',
    it: 'Rapporti',
    es: 'Informes',
    pt: 'Relatórios',
    nl: 'Rapporten',
    pl: 'Raporty',
  },
  'tour.reports.body': {
    en: 'Spare-parts usage, upcoming inspections, age warnings, and hose pressure test schedule.',
    de: 'Ersatzteile, anstehende Kontrollen, Altersgrenzen und Druckprüfungen.',
    fr: "Utilisation de pièces détachées, inspections à venir, alertes d'âge et calendrier des tests de pression.",
    it: 'Utilizzo ricambi, ispezioni in programma, avvisi di età e calendario prove di pressione.',
    es: 'Uso de repuestos, inspecciones próximas, advertencias de antigüedad y calendario de pruebas de presión.',
    pt: 'Utilização de peças sobressalentes, inspeções futuras, avisos de idade e calendário de testes de pressão.',
    nl: 'Gebruik van reserveonderdelen, aanstaande inspecties, leeftijdswaarschuwingen en schema voor drukproeven.',
    pl: 'Zużycie części zamiennych, nadchodzące inspekcje, ostrzeżenia o wieku i harmonogram prób ciśnieniowych.',
  },
  'tour.customer.title': {
    en: 'Customer Detail',
    de: 'Kundendetails',
    fr: 'Détail client',
    it: 'Dettaglio cliente',
    es: 'Detalle del cliente',
    pt: 'Detalhe do cliente',
    nl: 'Klantdetail',
    pl: 'Szczegóły klienta',
  },
  'tour.customer.body': {
    en: 'Full record: billing, object, owner addresses and janitor contact. Plus extinguishers, hoses and archive history.',
    de: 'Vollständiger Datensatz: Rechnungs-, Objekt-, Eigentümeradresse und Abwart. Dazu Feuerlöscher, Löschposten, Archive.',
    fr: 'Fiche complète : adresses de facturation, objet, propriétaire et contact concierge. Plus extincteurs, tuyaux et historique.',
    it: 'Scheda completa: indirizzi di fatturazione, oggetto, proprietario e contatto custode. Più estintori, manichette e archivio.',
    es: 'Registro completo: direcciones de facturación, objeto, propietario y contacto del conserje. Más extintores, mangueras e historial.',
    pt: 'Registo completo: endereços de faturação, objeto, proprietário e contacto do zelador. Mais extintores, mangueiras e histórico.',
    nl: 'Volledig dossier: facturering, object, eigenaar en conciërge. Plus brandblussers, slangen en archiefgeschiedenis.',
    pl: 'Pełny zapis: adresy rozliczeniowe, obiekt, właściciel i kontakt z dozorcą. Plus gaśnice, węże i historia archiwum.',
  },
  'tour.addExt.title': {
    en: 'Add Extinguisher',
    de: 'Feuerlöscher anlegen',
    fr: 'Ajouter un extincteur',
    it: 'Aggiungi estintore',
    es: 'Añadir extintor',
    pt: 'Adicionar extintor',
    nl: 'Brandblusser toevoegen',
    pl: 'Dodaj gaśnicę',
  },
  'tour.addExt.body': {
    en: 'Cascade selector: manufacturer → agent type → model → device type. Documentation appears automatically.',
    de: 'Kaskadenauswahl: Hersteller → Löschmittel → Modell → Gruppe. Dokumentation erscheint automatisch.',
    fr: "Sélecteur en cascade : fabricant → type d'agent → modèle → type d'appareil. La documentation apparaît automatiquement.",
    it: 'Selettore a cascata: produttore → tipo agente → modello → tipo dispositivo. La documentazione appare automaticamente.',
    es: 'Selector en cascada: fabricante → tipo de agente → modelo → tipo de dispositivo. La documentación aparece automáticamente.',
    pt: 'Seletor em cascata: fabricante → tipo de agente → modelo → tipo de dispositivo. A documentação aparece automaticamente.',
    nl: 'Cascadeselector: fabrikant → blusmiddeltype → model → apparaattype. Documentatie verschijnt automatisch.',
    pl: 'Selektor kaskadowy: producent → typ środka → model → typ urządzenia. Dokumentacja pojawia się automatycznie.',
  },
  'tour.editExt.title': {
    en: 'Extinguisher Editor',
    de: 'Feuerlöscher-Editor',
    fr: "Éditeur d'extincteur",
    it: 'Editor estintore',
    es: 'Editor de extintor',
    pt: 'Editor de extintor',
    nl: 'Blusser-editor',
    pl: 'Edytor gaśnicy',
  },
  'tour.editExt.body': {
    en: 'Age warnings fire automatically based on device type limits. Spare parts aggregate by article number.',
    de: 'Altersgrenzen automatisch je nach Gerätetyp. Ersatzteile werden pro Artikel-Nr. zusammengefasst.',
    fr: "Les alertes d'âge se déclenchent automatiquement selon les limites du type d'appareil. Les pièces sont agrégées par numéro d'article.",
    it: 'Gli avvisi di età si attivano automaticamente in base ai limiti del tipo di dispositivo. I ricambi si aggregano per numero articolo.',
    es: 'Las advertencias de antigüedad se activan automáticamente según los límites del tipo de dispositivo. Los repuestos se agrupan por número de artículo.',
    pt: 'Os avisos de idade disparam automaticamente com base nos limites do tipo de dispositivo. As peças são agregadas por número de artigo.',
    nl: 'Leeftijdswaarschuwingen activeren automatisch op basis van apparaattype-limieten. Onderdelen worden per artikelnummer geaggregeerd.',
    pl: 'Ostrzeżenia o wieku uruchamiają się automatycznie na podstawie limitów typu urządzenia. Części zamienne są agregowane według numeru artykułu.',
  },
  'tour.invoice.title': {
    en: 'Invoice',
    de: 'Rechnung',
    fr: 'Facture',
    it: 'Fattura',
    es: 'Factura',
    pt: 'Fatura',
    nl: 'Factuur',
    pl: 'Faktura',
  },
  'tour.invoice.body': {
    en: 'Generate invoices with country-specific VAT and rounding. Billing, object and owner blocks. Print and PDF export ready.',
    de: 'Rechnungen mit landesspezifischer MwSt. und Rundung. Rechnungs-, Objekt-, Eigentümerblock. Druck- und PDF-Export.',
    fr: 'Générez des factures avec TVA et arrondi par pays. Blocs facturation, objet et propriétaire. Impression et export PDF.',
    it: 'Genera fatture con IVA e arrotondamento specifici per paese. Blocchi fatturazione, oggetto e proprietario. Stampa ed esportazione PDF.',
    es: 'Genere facturas con IVA y redondeo específicos por país. Bloques de facturación, objeto y propietario. Impresión y exportación PDF.',
    pt: 'Gere faturas com IVA e arredondamento específicos por país. Blocos de faturação, objeto e proprietário. Impressão e exportação PDF.',
    nl: 'Genereer facturen met landspecifieke BTW en afronding. Facturerings-, object- en eigenaarblokken. Print en PDF-export.',
    pl: 'Generuj faktury z VAT i zaokrąglaniem specyficznym dla kraju. Bloki rozliczeniowe, obiektowe i właściciela. Druk i eksport PDF.',
  },
  'tour.offline.title': {
    en: 'Offline-ready',
    de: 'Offline-fähig',
    fr: 'Hors ligne',
    it: 'Offline',
    es: 'Sin conexión',
    pt: 'Offline',
    nl: 'Offline-klaar',
    pl: 'Tryb offline',
  },
  'tour.offline.body': {
    en: 'Toggle offline to simulate field work without network. Data persists locally; syncs when you reconnect.',
    de: 'Offline umschalten für Feldarbeit ohne Netz. Daten lokal gespeichert, Synchronisation bei Verbindung.',
    fr: 'Basculez hors ligne pour simuler le travail de terrain. Les données sont conservées localement et synchronisées à la reconnexion.',
    it: 'Passa offline per simulare il lavoro sul campo. I dati persistono localmente e si sincronizzano alla riconnessione.',
    es: 'Active sin conexión para simular trabajo de campo. Los datos persisten localmente y se sincronizan al reconectar.',
    pt: 'Alterne para offline para simular trabalho de campo. Os dados persistem localmente e sincronizam ao reconectar.',
    nl: 'Schakel offline om veldwerk te simuleren. Gegevens blijven lokaal opgeslagen en synchroniseren bij herverbinding.',
    pl: 'Przełącz offline, aby symulować pracę w terenie. Dane są przechowywane lokalnie i synchronizowane po ponownym połączeniu.',
  },
  'tour.lang.title': {
    en: 'Language',
    de: 'Sprache',
    fr: 'Langue',
    it: 'Lingua',
    es: 'Idioma',
    pt: 'Idioma',
    nl: 'Taal',
    pl: 'Język',
  },
  'tour.lang.body': {
    en: 'Switch between 8 languages. All labels update instantly.',
    de: 'Zwischen 8 Sprachen wechseln. Alle Bezeichnungen ändern sofort.',
    fr: 'Basculez entre 8 langues. Toutes les étiquettes se mettent à jour instantanément.',
    it: 'Passa tra 8 lingue. Tutte le etichette si aggiornano istantaneamente.',
    es: 'Cambie entre 8 idiomas. Todas las etiquetas se actualizan al instante.',
    pt: 'Alterne entre 8 idiomas. Todos os rótulos atualizam instantaneamente.',
    nl: 'Schakel tussen 8 talen. Alle labels worden direct bijgewerkt.',
    pl: 'Przełączaj między 8 językami. Wszystkie etykiety aktualizują się natychmiast.',
  },
  'tour.ready.title': {
    en: "You're ready",
    de: 'Startklar',
    fr: 'Vous êtes prêt',
    it: 'Sei pronto',
    es: 'Estás listo',
    pt: 'Está pronto',
    nl: 'U bent klaar',
    pl: 'Gotowe',
  },
  'tour.ready.body': {
    en: 'Click the Tour button any time to replay this walkthrough.',
    de: 'Über Tour jederzeit erneut starten.',
    fr: 'Cliquez sur le bouton Visite à tout moment pour relancer cette présentation.',
    it: 'Clicca sul pulsante Tour in qualsiasi momento per ripetere questa guida.',
    es: 'Haga clic en el botón Tour en cualquier momento para repetir esta guía.',
    pt: 'Clique no botão Tour a qualquer momento para repetir esta apresentação.',
    nl: 'Klik op de Tour-knop om deze rondleiding opnieuw af te spelen.',
    pl: 'Kliknij przycisk Tour w dowolnym momencie, aby powtórzyć ten przewodnik.',
  },

  // Calendar / Visits
  'nav.calendar': {
    en: 'Calendar',
    de: 'Kalender',
    fr: 'Calendrier',
    it: 'Calendario',
    es: 'Calendario',
    pt: 'Calendário',
    nl: 'Kalender',
    pl: 'Kalendarz',
  },
  'calendar.title': {
    en: 'Visit Calendar',
    de: 'Besuchskalender',
    fr: 'Calendrier des visites',
    it: 'Calendario visite',
    es: 'Calendario de visitas',
    pt: 'Calendário de visitas',
    nl: 'Bezoekkalender',
    pl: 'Kalendarz wizyt',
  },
  'calendar.today': {
    en: 'Today',
    de: 'Heute',
    fr: "Aujourd'hui",
    it: 'Oggi',
    es: 'Hoy',
    pt: 'Hoje',
    nl: 'Vandaag',
    pl: 'Dziś',
  },
  'calendar.addVisit': {
    en: 'Add Visit',
    de: 'Besuch hinzufügen',
    fr: 'Ajouter une visite',
    it: 'Aggiungi visita',
    es: 'Añadir visita',
    pt: 'Adicionar visita',
    nl: 'Bezoek toevoegen',
    pl: 'Dodaj wizytę',
  },
  'calendar.editVisit': {
    en: 'Edit Visit',
    de: 'Besuch bearbeiten',
    fr: 'Modifier la visite',
    it: 'Modifica visita',
    es: 'Editar visita',
    pt: 'Editar visita',
    nl: 'Bezoek bewerken',
    pl: 'Edytuj wizytę',
  },
  'calendar.deleteVisit': {
    en: 'Delete Visit',
    de: 'Besuch löschen',
    fr: 'Supprimer la visite',
    it: 'Elimina visita',
    es: 'Eliminar visita',
    pt: 'Excluir visita',
    nl: 'Bezoek verwijderen',
    pl: 'Usuń wizytę',
  },
  'calendar.customer': {
    en: 'Customer',
    de: 'Kunde',
    fr: 'Client',
    it: 'Cliente',
    es: 'Cliente',
    pt: 'Cliente',
    nl: 'Klant',
    pl: 'Klient',
  },
  'calendar.worker': {
    en: 'Technician',
    de: 'Techniker',
    fr: 'Technicien',
    it: 'Tecnico',
    es: 'Técnico',
    pt: 'Técnico',
    nl: 'Monteur',
    pl: 'Technik',
  },
  'calendar.date': {
    en: 'Date',
    de: 'Datum',
    fr: 'Date',
    it: 'Data',
    es: 'Fecha',
    pt: 'Data',
    nl: 'Datum',
    pl: 'Data',
  },
  'calendar.notes': {
    en: 'Notes',
    de: 'Notizen',
    fr: 'Notes',
    it: 'Note',
    es: 'Notas',
    pt: 'Notas',
    nl: 'Notities',
    pl: 'Notatki',
  },
  'calendar.status': {
    en: 'Status',
    de: 'Status',
    fr: 'Statut',
    it: 'Stato',
    es: 'Estado',
    pt: 'Estado',
    nl: 'Status',
    pl: 'Status',
  },
  'calendar.status.planned': {
    en: 'Planned',
    de: 'Geplant',
    fr: 'Planifié',
    it: 'Pianificato',
    es: 'Planificado',
    pt: 'Planeado',
    nl: 'Gepland',
    pl: 'Zaplanowana',
  },
  'calendar.status.confirmed': {
    en: 'Confirmed',
    de: 'Bestätigt',
    fr: 'Confirmé',
    it: 'Confermato',
    es: 'Confirmado',
    pt: 'Confirmado',
    nl: 'Bevestigd',
    pl: 'Potwierdzona',
  },
  'calendar.status.completed': {
    en: 'Completed',
    de: 'Abgeschlossen',
    fr: 'Terminé',
    it: 'Completato',
    es: 'Completado',
    pt: 'Concluído',
    nl: 'Voltooid',
    pl: 'Zakończona',
  },
  'calendar.status.cancelled': {
    en: 'Cancelled',
    de: 'Abgesagt',
    fr: 'Annulé',
    it: 'Annullato',
    es: 'Cancelado',
    pt: 'Cancelado',
    nl: 'Geannuleerd',
    pl: 'Anulowana',
  },
  'calendar.noVisits': {
    en: 'No visits scheduled',
    de: 'Keine Besuche geplant',
    fr: 'Aucune visite prévue',
    it: 'Nessuna visita programmata',
    es: 'No hay visitas programadas',
    pt: 'Nenhuma visita agendada',
    nl: 'Geen bezoeken gepland',
    pl: 'Brak zaplanowanych wizyt',
  },
  'calendar.allWorkers': {
    en: 'All technicians',
    de: 'Alle Techniker',
    fr: 'Tous les techniciens',
    it: 'Tutti i tecnici',
    es: 'Todos los técnicos',
    pt: 'Todos os técnicos',
    nl: 'Alle monteurs',
    pl: 'Wszyscy technicy',
  },
  'calendar.save': {
    en: 'Save',
    de: 'Speichern',
    fr: 'Enregistrer',
    it: 'Salva',
    es: 'Guardar',
    pt: 'Salvar',
    nl: 'Opslaan',
    pl: 'Zapisz',
  },
  'calendar.cancel': {
    en: 'Cancel',
    de: 'Abbrechen',
    fr: 'Annuler',
    it: 'Annulla',
    es: 'Cancelar',
    pt: 'Cancelar',
    nl: 'Annuleren',
    pl: 'Anuluj',
  },
  'calendar.visitSaved': {
    en: 'Visit saved',
    de: 'Besuch gespeichert',
    fr: 'Visite enregistrée',
    it: 'Visita salvata',
    es: 'Visita guardada',
    pt: 'Visita salva',
    nl: 'Bezoek opgeslagen',
    pl: 'Wizyta zapisana',
  },
  'calendar.visitDeleted': {
    en: 'Visit deleted',
    de: 'Besuch gelöscht',
    fr: 'Visite supprimée',
    it: 'Visita eliminata',
    es: 'Visita eliminada',
    pt: 'Visita excluída',
    nl: 'Bezoek verwijderd',
    pl: 'Wizyta usunięta',
  },
  'calendar.selectCustomer': {
    en: 'Select customer',
    de: 'Kunde auswählen',
    fr: 'Sélectionner un client',
    it: 'Seleziona cliente',
    es: 'Seleccionar cliente',
    pt: 'Selecionar cliente',
    nl: 'Klant selecteren',
    pl: 'Wybierz klienta',
  },

  // Client feedback v1
  'overview.total': {
    en: 'Total',
    de: 'Total',
    fr: 'Total',
    it: 'Totale',
    es: 'Total',
    pt: 'Total',
    nl: 'Totaal',
    pl: 'Łącznie',
  },
  'customer.serviceContract': {
    en: 'Service Contract',
    de: 'Servicevertrag',
    fr: 'Contrat de service',
    it: 'Contratto di servizio',
    es: 'Contrato de servicio',
    pt: 'Contrato de serviço',
    nl: 'Servicecontract',
    pl: 'Umowa serwisowa',
  },
  'customer.uploadContract': {
    en: 'Upload Contract PDF',
    de: 'Vertrag PDF hochladen',
    fr: 'Télécharger PDF contrat',
    it: 'Carica PDF contratto',
    es: 'Subir PDF contrato',
    pt: 'Carregar PDF contrato',
    nl: 'Contract PDF uploaden',
    pl: 'Prześlij PDF umowy',
  },
  'customer.viewContract': {
    en: 'View Contract',
    de: 'Vertrag anzeigen',
    fr: 'Voir le contrat',
    it: 'Visualizza contratto',
    es: 'Ver contrato',
    pt: 'Ver contrato',
    nl: 'Contract bekijken',
    pl: 'Zobacz umowę',
  },
  'customer.contractUploaded': {
    en: 'Contract PDF uploaded',
    de: 'Vertrag PDF hochgeladen',
    fr: 'PDF contrat téléchargé',
    it: 'PDF contratto caricato',
    es: 'PDF contrato subido',
    pt: 'PDF contrato carregado',
    nl: 'Contract PDF geüpload',
    pl: 'PDF umowy przesłany',
  },
  'customer.linkedCustomers': {
    en: 'Linked Customers',
    de: 'Verknüpfte Kunden',
    fr: 'Clients liés',
    it: 'Clienti collegati',
    es: 'Clientes vinculados',
    pt: 'Clientes vinculados',
    nl: 'Gekoppelde klanten',
    pl: 'Powiązani klienci',
  },
  'customer.linkCustomer': {
    en: 'Link customer…',
    de: 'Kunde verknüpfen…',
    fr: 'Lier un client…',
    it: 'Collega cliente…',
    es: 'Vincular cliente…',
    pt: 'Vincular cliente…',
    nl: 'Klant koppelen…',
    pl: 'Powiąż klienta…',
  },
  'customer.searchCustomers': {
    en: 'Search customers…',
    de: 'Kunden suchen…',
    fr: 'Rechercher clients…',
    it: 'Cerca clienti…',
    es: 'Buscar clientes…',
    pt: 'Pesquisar clientes…',
    nl: 'Klanten zoeken…',
    pl: 'Szukaj klientów…',
  },
  'customer.noLinkedCustomers': {
    en: 'No linked customers',
    de: 'Keine verknüpften Kunden',
    fr: 'Aucun client lié',
    it: 'Nessun cliente collegato',
    es: 'Sin clientes vinculados',
    pt: 'Sem clientes vinculados',
    nl: 'Geen gekoppelde klanten',
    pl: 'Brak powiązanych klientów',
  },
  'ext.maintainedThisCycle': {
    en: 'Maintained this cycle',
    de: 'In diesem Zyklus gewartet',
    fr: 'Entretenu ce cycle',
    it: 'Manutenuto in questo ciclo',
    es: 'Mantenido este ciclo',
    pt: 'Mantido neste ciclo',
    nl: 'Onderhouden deze cyclus',
    pl: 'Konserwowane w tym cyklu',
  },
  'ext.notMaintained': {
    en: 'Not yet maintained',
    de: 'Noch nicht gewartet',
    fr: 'Pas encore entretenu',
    it: 'Non ancora manutenuto',
    es: 'Aún no mantenido',
    pt: 'Ainda não mantido',
    nl: 'Nog niet onderhouden',
    pl: 'Jeszcze nie konserwowane',
  },
  'hose.hoseDiameter': {
    en: 'Hose Diameter',
    de: 'Schlauchdurchmesser',
    fr: 'Diamètre tuyau',
    it: 'Diametro tubo',
    es: 'Diámetro manguera',
    pt: 'Diâmetro mangueira',
    nl: 'Slangdiameter',
    pl: 'Średnica węża',
  },
  'hose.connectionDiameter': {
    en: 'Connection Diameter',
    de: 'Anschlussdurchmesser',
    fr: 'Diamètre raccord',
    it: 'Diametro raccordo',
    es: 'Diámetro conexión',
    pt: 'Diâmetro conexão',
    nl: 'Aansluitdiameter',
    pl: 'Średnica złącza',
  },
  'ext.uploadDoc': {
    en: 'Upload Documentation PDF',
    de: 'Dokumentation PDF hochladen',
    fr: 'Télécharger documentation PDF',
    it: 'Carica documentazione PDF',
    es: 'Subir documentación PDF',
    pt: 'Carregar documentação PDF',
    nl: 'Documentatie PDF uploaden',
    pl: 'Prześlij PDF dokumentacji',
  },
  'ext.viewDoc': {
    en: 'View Documentation PDF',
    de: 'Dokumentation PDF anzeigen',
    fr: 'Voir documentation PDF',
    it: 'Visualizza documentazione PDF',
    es: 'Ver documentación PDF',
    pt: 'Ver documentação PDF',
    nl: 'Documentatie PDF bekijken',
    pl: 'Zobacz PDF dokumentacji',
  },
  'ext.mfrSearch': {
    en: 'Search manufacturers…',
    de: 'Hersteller suchen…',
    fr: 'Rechercher fabricants…',
    it: 'Cerca produttori…',
    es: 'Buscar fabricantes…',
    pt: 'Pesquisar fabricantes…',
    nl: 'Fabrikanten zoeken…',
    pl: 'Szukaj producentów…',
  },
  'table.refilled': {
    en: 'Refilled',
    de: 'Befüllt',
    fr: 'Rechargé',
    it: 'Ricaricato',
    es: 'Recargado',
    pt: 'Recarregado',
    nl: 'Hervuld',
    pl: 'Napełniony',
  },

  // Updates panel tabs + Changes items
  'updates.tab.feedback': {
    en: 'Feedback',
    de: 'Feedback',
    fr: 'Retours',
    it: 'Feedback',
    es: 'Comentarios',
    pt: 'Feedback',
    nl: 'Feedback',
    pl: 'Opinie',
  },
  'updates.tab.changes': {
    en: 'Changes',
    de: 'Änderungen',
    fr: 'Changements',
    it: 'Modifiche',
    es: 'Cambios',
    pt: 'Alterações',
    nl: 'Wijzigingen',
    pl: 'Zmiany',
  },
  'updates.fix.section.data': {
    en: 'Data',
    de: 'Daten',
    fr: 'Données',
    it: 'Dati',
    es: 'Datos',
    pt: 'Dados',
    nl: 'Gegevens',
    pl: 'Dane',
  },
  'updates.fix.section.pwa': {
    en: 'PWA',
    de: 'PWA',
    fr: 'PWA',
    it: 'PWA',
    es: 'PWA',
    pt: 'PWA',
    nl: 'PWA',
    pl: 'PWA',
  },
  'updates.fix.checkUpdate': {
    en: 'Check for updates button in sidebar',
    de: 'Update-Prüfung Schaltfläche in Seitenleiste',
    fr: 'Bouton vérifier mises à jour dans la barre latérale',
    it: 'Pulsante verifica aggiornamenti nella barra laterale',
    es: 'Botón buscar actualizaciones en barra lateral',
    pt: 'Botão verificar atualizações na barra lateral',
    nl: 'Controleer updates knop in zijbalk',
    pl: 'Przycisk sprawdzania aktualizacji na pasku bocznym',
  },
  'updates.fix.section.admin': {
    en: 'Admin',
    de: 'Verwaltung',
    fr: 'Admin',
    it: 'Admin',
    es: 'Admin',
    pt: 'Admin',
    nl: 'Beheer',
    pl: 'Admin',
  },
  'updates.fix.export': {
    en: 'Export button downloads customers as JSON',
    de: 'Export-Button lädt Kunden als JSON herunter',
    fr: 'Bouton export télécharge les clients en JSON',
    it: 'Pulsante esporta scarica clienti in JSON',
    es: 'Botón exportar descarga clientes como JSON',
    pt: 'Botão exportar baixa clientes como JSON',
    nl: 'Exportknop downloadt klanten als JSON',
    pl: 'Przycisk eksportu pobiera klientów jako JSON',
  },
  'updates.fix.newCustomer': {
    en: 'New customer form: name, status, worker',
    de: 'Neuer Kunde: Name, Status, Mitarbeiter',
    fr: 'Nouveau client: nom, statut, technicien',
    it: 'Nuovo cliente: nome, stato, tecnico',
    es: 'Nuevo cliente: nombre, estado, técnico',
    pt: 'Novo cliente: nome, status, técnico',
    nl: 'Nieuwe klant: naam, status, monteur',
    pl: 'Nowy klient: nazwa, status, pracownik',
  },
  'updates.fix.invoiceEmail': {
    en: 'Invoice email opens mail client with subject + total',
    de: 'Rechnungs-E-Mail öffnet Mail-Client mit Betreff + Betrag',
    fr: 'Email facture ouvre mail client avec sujet + total',
    it: 'Email fattura apre client mail con oggetto + totale',
    es: 'Email factura abre correo con asunto + total',
    pt: 'Email fatura abre email com assunto + total',
    nl: 'Factuur e-mail opent mailclient met onderwerp + totaal',
    pl: 'Email faktury otwiera pocztę z tematem + sumą',
  },
  'updates.fix.hoseEmail': {
    en: 'Hose report email opens mail client with customer info',
    de: 'Schlauchbericht-E-Mail öffnet Mail-Client mit Kundendaten',
    fr: 'Email rapport tuyau ouvre mail client avec infos client',
    it: 'Email report tubo apre client mail con info cliente',
    es: 'Email reporte manguera abre correo con info cliente',
    pt: 'Email relatório mangueira abre email com info cliente',
    nl: 'Slangrapport e-mail opent mailclient met klantinfo',
    pl: 'Email raportu otwiera pocztę z danymi klienta',
  },
  'updates.fix.jsonImport': {
    en: 'JSON import reads file and replaces customers (with confirm)',
    de: 'JSON-Import liest Datei und ersetzt Kunden (mit Bestätigung)',
    fr: 'Import JSON lit le fichier et remplace les clients',
    it: 'Import JSON legge il file e sostituisce i clienti',
    es: 'Import JSON lee el archivo y reemplaza clientes',
    pt: 'Import JSON lê arquivo e substitui clientes',
    nl: 'JSON import leest bestand en vervangt klanten',
    pl: 'Import JSON czyta plik i zastępuje klientów',
  },
  'updates.fix.workers': {
    en: 'Workers: create, edit, delete — role + active toggle',
    de: 'Mitarbeiter: Erstellen, Bearbeiten, Löschen',
    fr: 'Techniciens: créer, modifier, supprimer',
    it: 'Tecnici: crea, modifica, elimina',
    es: 'Técnicos: crear, editar, eliminar',
    pt: 'Técnicos: criar, editar, excluir',
    nl: 'Medewerkers: aanmaken, bewerken, verwijderen',
    pl: 'Pracownicy: tworzenie, edycja, usuwanie',
  },
  'updates.fix.deviceTypes': {
    en: 'Device types: create, edit, delete — pressure class + age limit',
    de: 'Gerätetypen: Erstellen, Bearbeiten, Löschen',
    fr: 'Types appareils: créer, modifier, supprimer',
    it: 'Tipi dispositivi: crea, modifica, elimina',
    es: 'Tipos dispositivos: crear, editar, eliminar',
    pt: 'Tipos dispositivos: criar, editar, excluir',
    nl: 'Apparaattypen: aanmaken, bewerken, verwijderen',
    pl: 'Typy urządzeń: tworzenie, edycja, usuwanie',
  },
  'updates.fix.spareParts': {
    en: 'Spare parts: create, edit, delete — type + price',
    de: 'Ersatzteile: Erstellen, Bearbeiten, Löschen',
    fr: 'Pièces de rechange: créer, modifier, supprimer',
    it: 'Ricambi: crea, modifica, elimina',
    es: 'Repuestos: crear, editar, eliminar',
    pt: 'Peças: criar, editar, excluir',
    nl: 'Onderdelen: aanmaken, bewerken, verwijderen',
    pl: 'Części zamienne: tworzenie, edycja, usuwanie',
  },
  'updates.fix.servicePrices': {
    en: 'Service prices: create, edit, delete per device type',
    de: 'Servicepreise: Erstellen, Bearbeiten, Löschen',
    fr: 'Tarifs service: créer, modifier, supprimer',
    it: 'Prezzi servizio: crea, modifica, elimina',
    es: 'Precios servicio: crear, editar, eliminar',
    pt: 'Preços serviço: criar, editar, excluir',
    nl: 'Serviceprijzen: aanmaken, bewerken, verwijderen',
    pl: 'Ceny usług: tworzenie, edycja, usuwanie',
  },
  'updates.fix.manufacturers': {
    en: 'Manufacturers & models: full CRUD + delete model wired',
    de: 'Hersteller & Modelle: vollständiges CRUD aktiv',
    fr: 'Fabricants & modèles: CRUD complet actif',
    it: 'Produttori & modelli: CRUD completo attivo',
    es: 'Fabricantes y modelos: CRUD completo activo',
    pt: 'Fabricantes e modelos: CRUD completo ativo',
    nl: 'Fabrikanten & modellen: volledig CRUD actief',
    pl: 'Producenci i modele: pełny CRUD aktywny',
  },

  // Admin CRUD + IO
  'mfr.agentType': {
    en: 'Agent Type',
    de: 'Löschmitteltyp',
    fr: 'Type agent',
    it: 'Tipo agente',
    es: 'Tipo agente',
    pt: 'Tipo agente',
    nl: 'Type middel',
    pl: 'Typ środka',
  },
  'mfr.mfrName': {
    en: 'Manufacturer Name',
    de: 'Herstellername',
    fr: 'Nom du fabricant',
    it: 'Nome produttore',
    es: 'Nombre fabricante',
    pt: 'Nome fabricante',
    nl: 'Naam fabrikant',
    pl: 'Nazwa producenta',
  },
  'prices.qtyRange': {
    en: 'Qty Range',
    de: 'Mengenbereich',
    fr: 'Plage qté',
    it: 'Intervallo qtà',
    es: 'Rango cant.',
    pt: 'Intervalo qtd.',
    nl: 'Aantalsbereik',
    pl: 'Zakres ilości',
  },
  'io.importConfirm': {
    en: 'Replace all customer data with the imported file?',
    de: 'Alle Kundendaten durch die importierte Datei ersetzen?',
    fr: 'Remplacer toutes les données client par le fichier importé?',
    it: 'Sostituire tutti i dati con il file importato?',
    es: '¿Reemplazar todos los datos con el archivo importado?',
    pt: 'Substituir todos os dados pelo arquivo importado?',
    nl: 'Alle klantdata vervangen door het geïmporteerde bestand?',
    pl: 'Zastąpić wszystkie dane zaimportowanym plikiem?',
  },
  'io.importSuccess': {
    en: 'Import successful',
    de: 'Import erfolgreich',
    fr: 'Import réussi',
    it: 'Importazione riuscita',
    es: 'Importación exitosa',
    pt: 'Importação bem-sucedida',
    nl: 'Import geslaagd',
    pl: 'Import zakończony sukcesem',
  },
  'io.importError': {
    en: 'Invalid JSON format',
    de: 'Ungültiges JSON-Format',
    fr: 'Format JSON invalide',
    it: 'Formato JSON non valido',
    es: 'Formato JSON inválido',
    pt: 'Formato JSON inválido',
    nl: 'Ongeldig JSON-formaat',
    pl: 'Nieprawidłowy format JSON',
  },
  'io.excelSoon': {
    en: 'Excel import not yet available — use JSON format.',
    de: 'Excel-Import noch nicht verfügbar — JSON-Format verwenden.',
    fr: 'Import Excel pas encore disponible — utiliser JSON.',
    it: 'Import Excel non ancora disponibile — usare JSON.',
    es: 'Import Excel no disponible aún — usar formato JSON.',
    pt: 'Import Excel ainda não disponível — usar formato JSON.',
    nl: 'Excel import nog niet beschikbaar — gebruik JSON.',
    pl: 'Import Excel jeszcze niedostępny — użyj formatu JSON.',
  },
  'customer.newCustomerTitle': {
    en: 'New Customer',
    de: 'Neuer Kunde',
    fr: 'Nouveau client',
    it: 'Nuovo cliente',
    es: 'Nuevo cliente',
    pt: 'Novo cliente',
    nl: 'Nieuwe klant',
    pl: 'Nowy klient',
  },

  // Updates panel
  'updates.title': {
    en: 'Updates',
    de: 'Updates',
    fr: 'Mises à jour',
    it: 'Aggiornamenti',
    es: 'Actualizaciones',
    pt: 'Atualizações',
    nl: 'Updates',
    pl: 'Aktualizacje',
  },
  'updates.new': {
    en: 'New',
    de: 'Neu',
    fr: 'Nouveau',
    it: 'Nuovo',
    es: 'Nuevo',
    pt: 'Novo',
    nl: 'Nieuw',
    pl: 'Nowe',
  },
  'updates.subtitle': {
    en: 'All feedback from v1 report',
    de: 'Alle Rückmeldungen aus v1 Bericht',
    fr: 'Tous les retours du rapport v1',
    it: 'Tutti i feedback dal report v1',
    es: 'Todos los comentarios del informe v1',
    pt: 'Todos os comentários do relatório v1',
    nl: 'Alle feedback uit v1 rapport',
    pl: 'Wszystkie opinie z raportu v1',
  },
  'updates.section.overview': {
    en: 'Customer Overview',
    de: 'Kundenübersicht',
    fr: 'Aperçu clients',
    it: 'Panoramica clienti',
    es: 'Resumen clientes',
    pt: 'Visão geral clientes',
    nl: 'Klantenoverzicht',
    pl: 'Przegląd klientów',
  },
  'updates.overview.total': {
    en: 'Customer total column (sum of all devices)',
    de: 'Kundentotal-Spalte (Summe aller Geräte)',
    fr: 'Colonne total client (somme de tous les appareils)',
    it: 'Colonna totale cliente (somma di tutti i dispositivi)',
    es: 'Columna total cliente (suma de todos los dispositivos)',
    pt: 'Coluna total cliente (soma de todos os dispositivos)',
    nl: 'Klanttotaal kolom (som van alle apparaten)',
    pl: 'Kolumna łączna klienta (suma wszystkich urządzeń)',
  },
  'updates.overview.sort': {
    en: 'Sortable: City, Next Insp., Total columns',
    de: 'Sortierbar: Stadt, Nächste Kontr., Total',
    fr: 'Triable : Ville, Proch. insp., Total',
    it: 'Ordinabile: Città, Pross. isp., Totale',
    es: 'Ordenable: Ciudad, Próx. insp., Total',
    pt: 'Ordenável: Cidade, Próx. insp., Total',
    nl: 'Sorteerbaar: Stad, Volg. insp., Totaal',
    pl: 'Sortowanie: Miasto, Nast. kontr., Łącznie',
  },
  'updates.overview.zip': {
    en: 'ZIP code included in search',
    de: 'PLZ in Suche einbezogen',
    fr: 'Code postal inclus dans la recherche',
    it: 'CAP incluso nella ricerca',
    es: 'Código postal incluido en búsqueda',
    pt: 'CEP incluído na pesquisa',
    nl: 'Postcode in zoekopdracht',
    pl: 'Kod pocztowy w wyszukiwaniu',
  },
  'updates.overview.cities': {
    en: 'City filter dropdown removed',
    de: '"Alle Städte" Dropdown entfernt',
    fr: 'Filtre ville supprimé',
    it: 'Filtro città rimosso',
    es: 'Filtro ciudad eliminado',
    pt: 'Filtro cidade removido',
    nl: 'Stad filter verwijderd',
    pl: 'Filtr miast usunięty',
  },
  'updates.section.detail': {
    en: 'Customer Detail',
    de: 'Kundendetail',
    fr: 'Détail client',
    it: 'Dettaglio cliente',
    es: 'Detalle cliente',
    pt: 'Detalhe cliente',
    nl: 'Klantdetail',
    pl: 'Szczegóły klienta',
  },
  'updates.detail.contractLabel': {
    en: 'Service contract shows Yes/No instead of status label',
    de: 'Servicevertrag zeigt Ja/Nein statt Status-Label',
    fr: 'Contrat service affiche Oui/Non au lieu du statut',
    it: 'Contratto servizio mostra Sì/No invece dello stato',
    es: 'Contrato servicio muestra Sí/No en lugar del estado',
    pt: 'Contrato serviço mostra Sim/Não em vez do status',
    nl: 'Servicecontract toont Ja/Nee in plaats van status',
    pl: 'Umowa serwisowa pokazuje Tak/Nie zamiast statusu',
  },
  'updates.detail.contract': {
    en: 'Service contract field + PDF upload',
    de: 'Servicevertrag-Feld + PDF-Upload',
    fr: 'Champ contrat service + téléchargement PDF',
    it: 'Campo contratto servizio + caricamento PDF',
    es: 'Campo contrato servicio + carga PDF',
    pt: 'Campo contrato serviço + upload PDF',
    nl: 'Servicecontract veld + PDF upload',
    pl: 'Pole umowy serwisowej + przesyłanie PDF',
  },
  'updates.detail.dates': {
    en: 'Date format changed to DD.MM.YYYY',
    de: 'Datumsformat auf TT.MM.JJJJ geändert',
    fr: 'Format date changé en JJ.MM.AAAA',
    it: 'Formato data cambiato in GG.MM.AAAA',
    es: 'Formato fecha cambiado a DD.MM.AAAA',
    pt: 'Formato data alterado para DD.MM.AAAA',
    nl: 'Datumnotatie gewijzigd naar DD.MM.JJJJ',
    pl: 'Format daty zmieniony na DD.MM.RRRR',
  },
  'updates.section.ext': {
    en: 'Extinguishers',
    de: 'Feuerlöscher',
    fr: 'Extincteurs',
    it: 'Estintori',
    es: 'Extintores',
    pt: 'Extintores',
    nl: 'Brandblussers',
    pl: 'Gaśnice',
  },
  'updates.ext.refill': {
    en: 'Last refill date column added',
    de: 'Spalte letzte Befüllung hinzugefügt',
    fr: 'Colonne dernière recharge ajoutée',
    it: 'Colonna ultima ricarica aggiunta',
    es: 'Columna última recarga añadida',
    pt: 'Coluna última recarga adicionada',
    nl: 'Kolom laatste vulling toegevoegd',
    pl: 'Dodano kolumnę ostatniego napełnienia',
  },
  'updates.ext.age': {
    en: 'Age limit warning now orange on year number',
    de: 'Alterswarnung jetzt orange bei Jahreszahl',
    fr: 'Avertissement âge maintenant orange sur année',
    it: 'Avviso età ora arancione sul numero anno',
    es: 'Aviso de edad ahora naranja en el año',
    pt: 'Aviso de idade agora laranja no número do ano',
    nl: 'Leeftijdswaarschuwing nu oranje op jaartal',
    pl: 'Ostrzeżenie wiekowe teraz pomarańczowe na roku',
  },
  'updates.ext.mfrsearch': {
    en: 'Searchable manufacturer dropdown',
    de: 'Durchsuchbare Hersteller-Auswahl',
    fr: 'Liste fabricants avec recherche',
    it: 'Elenco produttori con ricerca',
    es: 'Lista fabricantes con búsqueda',
    pt: 'Lista fabricantes com pesquisa',
    nl: 'Doorzoekbare fabrikanten lijst',
    pl: 'Wyszukiwalna lista producentów',
  },
  'updates.ext.docpdf': {
    en: 'Device documentation PDF upload',
    de: 'Gerätedokumentation PDF-Upload',
    fr: 'Téléchargement PDF documentation appareil',
    it: 'Caricamento PDF documentazione dispositivo',
    es: 'Carga PDF documentación dispositivo',
    pt: 'Upload PDF documentação dispositivo',
    nl: 'Apparaatdocumentatie PDF upload',
    pl: 'Przesyłanie PDF dokumentacji urządzenia',
  },
  'updates.section.hose': {
    en: 'Fire Hoses',
    de: 'Löschposten',
    fr: 'Lances incendie',
    it: 'Manichette antincendio',
    es: 'Mangueras contra incendios',
    pt: 'Mangueiras de incêndio',
    nl: 'Brandslangen',
    pl: 'Węże pożarnicze',
  },
  'updates.hose.options': {
    en: 'Hose Options admin page — configure diameters & models',
    de: 'Schlauchoptionen-Seite — Durchmesser & Modelle konfigurieren',
    fr: 'Page options tuyaux — configurer diamètres & modèles',
    it: 'Pagina opzioni manichetta — configura diametri e modelli',
    es: 'Página opciones manguera — configurar diámetros y modelos',
    pt: 'Página opções mangueira — configurar diâmetros e modelos',
    nl: 'Slangopties-pagina — diameters & modellen configureren',
    pl: 'Strona opcji węży — konfiguruj średnice i modele',
  },
  'updates.hose.diameter': {
    en: 'Hose + connection diameter split into two dropdowns (16/24, 19/27, 25/34 mm)',
    de: 'Schlauch- und Anschlussdurchmesser getrennt (16/24, 19/27, 25/34 mm)',
    fr: 'Diamètre tuyau + raccord séparés (16/24, 19/27, 25/34 mm)',
    it: 'Diametri tubo + raccordo separati (16/24, 19/27, 25/34 mm)',
    es: 'Diámetros manguera + conexión separados (16/24, 19/27, 25/34 mm)',
    pt: 'Diâmetros mangueira + conexão separados (16/24, 19/27, 25/34 mm)',
    nl: 'Slang + aansluitdiameter gesplitst (16/24, 19/27, 25/34 mm)',
    pl: 'Średnice węża + złącza rozdzielone (16/24, 19/27, 25/34 mm)',
  },
  'updates.section.invoice': {
    en: 'Invoice',
    de: 'Rechnung',
    fr: 'Facture',
    it: 'Fattura',
    es: 'Factura',
    pt: 'Fatura',
    nl: 'Factuur',
    pl: 'Faktura',
  },
  'updates.focusHint': {
    en: 'Click anywhere to dismiss',
    de: 'Klicken zum Schließen',
    fr: "Cliquez n'importe où pour fermer",
    it: 'Clicca ovunque per chiudere',
    es: 'Clic en cualquier lugar para cerrar',
    pt: 'Clique em qualquer lugar para fechar',
    nl: 'Klik ergens om te sluiten',
    pl: 'Kliknij gdziekolwiek, aby zamknąć',
  },
  'updates.invoice.price': {
    en: 'Line item prices are editable (discount support)',
    de: 'Positionspreise bearbeitbar (Rabatt möglich)',
    fr: 'Prix des postes modifiables (remise possible)',
    it: 'Prezzi posizioni modificabili (sconto possibile)',
    es: 'Precios de línea editables (soporte descuento)',
    pt: 'Preços de linha editáveis (suporte desconto)',
    nl: 'Regelprijs bewerkbaar (kortingsondersteuning)',
    pl: 'Ceny pozycji edytowalne (obsługa rabatu)',
  },
  'updates.section.customerDetail': {
    en: 'Customer Detail',
    de: 'Kundendetail',
    fr: 'Détail client',
    it: 'Dettaglio cliente',
    es: 'Detalle del cliente',
    pt: 'Detalhe do cliente',
    nl: 'Klantdetail',
    pl: 'Szczegóły klienta',
  },
  'updates.detail.contractToggle': {
    en: 'Service contract is now a toggle switch (on/off)',
    de: 'Servicevertrag ist jetzt ein Umschalter (ein/aus)',
    fr: 'Contrat de service est maintenant un interrupteur (on/off)',
    it: 'Contratto di servizio ora è un interruttore (on/off)',
    es: 'Contrato de servicio ahora es un interruptor (on/off)',
    pt: 'Contrato de serviço agora é um interruptor (on/off)',
    nl: 'Servicecontract is nu een schakelaar (aan/uit)',
    pl: 'Umowa serwisowa jest teraz przełącznikiem (wł./wył.)',
  },
  'updates.detail.contractPdfGated': {
    en: 'PDF upload only visible when service contract is active',
    de: 'PDF-Upload nur sichtbar wenn Servicevertrag aktiv',
    fr: 'Téléchargement PDF visible uniquement si contrat actif',
    it: 'Caricamento PDF visibile solo con contratto attivo',
    es: 'Carga PDF visible solo con contrato activo',
    pt: 'Upload PDF visível apenas com contrato ativo',
    nl: 'PDF-upload alleen zichtbaar bij actief contract',
    pl: 'Upload PDF widoczny tylko przy aktywnym kontrakcie',
  },
  'updates.detail.linkedCustomers': {
    en: 'Link related customers together (bidirectional)',
    de: 'Verwandte Kunden verknüpfen (bidirektional)',
    fr: 'Lier des clients associés (bidirectionnel)',
    it: 'Collegare clienti correlati (bidirezionale)',
    es: 'Vincular clientes relacionados (bidireccional)',
    pt: 'Vincular clientes relacionados (bidirecional)',
    nl: 'Gerelateerde klanten koppelen (bidirectioneel)',
    pl: 'Łącz powiązanych klientów (dwukierunkowe)',
  },
  'updates.section.extStatus': {
    en: 'Fire Extinguishers',
    de: 'Feuerlöscher',
    fr: 'Extincteurs',
    it: 'Estintori',
    es: 'Extintores',
    pt: 'Extintores',
    nl: 'Brandblussers',
    pl: 'Gaśnice',
  },
  'updates.ext.statusCol': {
    en: 'Status column: checkmark = maintained this cycle, ✕ = not yet',
    de: 'Statusspalte: Haken = in diesem Zyklus gewartet, ✕ = noch nicht',
    fr: 'Colonne statut: coche = entretenu ce cycle, ✕ = pas encore',
    it: 'Colonna stato: spunta = manutenuto ciclo, ✕ = non ancora',
    es: 'Columna estado: marca = mantenido este ciclo, ✕ = aún no',
    pt: 'Coluna estado: marca = mantido neste ciclo, ✕ = ainda não',
    nl: 'Statuskolom: vinkje = onderhouden dit cyclus, ✕ = nog niet',
    pl: 'Kolumna statusu: ptaszek = konserwowane, ✕ = jeszcze nie',
  },
  'updates.ext.sortHeaders': {
    en: 'Click any column header to sort extinguishers',
    de: 'Spaltenüberschrift klicken zum Sortieren der Löscher',
    fr: 'Cliquer sur un en-tête de colonne pour trier les extincteurs',
    it: "Clic sull'intestazione per ordinare gli estintori",
    es: 'Clic en encabezado de columna para ordenar extintores',
    pt: 'Clique no cabeçalho da coluna para ordenar extintores',
    nl: 'Klik op kolomkop om brandblussers te sorteren',
    pl: 'Kliknij nagłówek kolumny aby posortować gaśnice',
  },
  'auth.signIn': {
    en: 'Sign in',
    de: 'Anmelden',
    fr: 'Se connecter',
    it: 'Accedi',
    es: 'Iniciar sesión',
    pt: 'Entrar',
    nl: 'Inloggen',
    pl: 'Zaloguj się',
  },
  'auth.signOut': {
    en: 'Sign out',
    de: 'Abmelden',
    fr: 'Se déconnecter',
    it: 'Esci',
    es: 'Cerrar sesión',
    pt: 'Sair',
    nl: 'Uitloggen',
    pl: 'Wyloguj się',
  },
  'auth.email': {
    en: 'Email',
    de: 'E-Mail',
    fr: 'E-mail',
    it: 'Email',
    es: 'Correo',
    pt: 'E-mail',
    nl: 'E-mail',
    pl: 'Email',
  },
  'auth.password': {
    en: 'Password',
    de: 'Passwort',
    fr: 'Mot de passe',
    it: 'Password',
    es: 'Contraseña',
    pt: 'Senha',
    nl: 'Wachtwoord',
    pl: 'Hasło',
  },
  'auth.signingIn': {
    en: 'Signing in…',
    de: 'Anmelden…',
    fr: 'Connexion…',
    it: 'Accesso…',
    es: 'Iniciando…',
    pt: 'A entrar…',
    nl: 'Inloggen…',
    pl: 'Logowanie…',
  },
  'auth.forgot': {
    en: 'Forgot password?',
    de: 'Passwort vergessen?',
    fr: 'Mot de passe oublié ?',
    it: 'Password dimenticata?',
    es: '¿Olvidaste tu contraseña?',
    pt: 'Esqueceu a senha?',
    nl: 'Wachtwoord vergeten?',
    pl: 'Zapomniałeś hasła?',
  },
  'auth.forgotSent': {
    en: 'If the email exists, a reset link is on its way.',
    de: 'Wenn die E-Mail existiert, wird ein Link gesendet.',
    fr: "Si l'e-mail existe, un lien vous sera envoyé.",
    it: "Se l'email esiste, riceverai un link.",
    es: 'Si el correo existe, recibirás un enlace.',
    pt: 'Se o email existir, enviaremos um link.',
    nl: 'Als het e-mailadres bestaat, sturen we een link.',
    pl: 'Jeśli e-mail istnieje, wyślemy link.',
  },
  'auth.invalidCreds': {
    en: 'Invalid credentials',
    de: 'Ungültige Anmeldedaten',
    fr: 'Identifiants invalides',
    it: 'Credenziali non valide',
    es: 'Credenciales no válidas',
    pt: 'Credenciais inválidas',
    nl: 'Ongeldige gegevens',
    pl: 'Nieprawidłowe dane',
  },
  'auth.loginFailed': {
    en: 'Login failed. Please try again.',
    de: 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
    fr: 'Échec de la connexion. Réessayez.',
    it: 'Accesso non riuscito. Riprova.',
    es: 'Error al iniciar sesión. Reintentar.',
    pt: 'Falha no login. Tente novamente.',
    nl: 'Inloggen mislukt. Probeer opnieuw.',
    pl: 'Logowanie nie powiodło się.',
  },
  'auth.enterEmailFirst': {
    en: 'Enter your email above first',
    de: 'Bitte zuerst E-Mail eingeben',
    fr: "Saisissez d'abord votre e-mail",
    it: 'Inserisci prima la tua email',
    es: 'Introduce tu correo arriba',
    pt: 'Introduza o seu e-mail acima',
    nl: 'Voer eerst je e-mail in',
    pl: 'Najpierw wpisz e-mail',
  },
  'auth.loading': {
    en: 'Loading…',
    de: 'Lädt…',
    fr: 'Chargement…',
    it: 'Caricamento…',
    es: 'Cargando…',
    pt: 'A carregar…',
    nl: 'Laden…',
    pl: 'Ładowanie…',
  },
  'auth.brandTagline': L('Inspections, on the job'),
  'auth.heroTitle': L('Every extinguisher, every hose, every visit.'),
  'auth.heroSubtitle': L(
    'One app for the whole crew — from the truck to the basement.'
  ),
  'auth.featureSecure': L('Your customers, your data, your team only'),
  'auth.featureOffline': L('Works underground. Syncs when you surface.'),
  'auth.signInSubtitle': L('Sign in with your work email.'),
  'auth.showPw': L('Show password'),
  'auth.hidePw': L('Hide password'),
  'auth.footerNote': L('Need access? Ask your organization admin.'),
  // Superadmin nav
  'nav.superadmin': L('Superadmin'),
  'nav.tenants': L('Tenants'),
  'nav.platformAudit': L('Platform Audit'),
  // Superadmin tenants list
  'superadmin.tenants.title': L('Tenants'),
  'superadmin.tenants.subtitle': L('Manage all customer organisations.'),
  'superadmin.tenants.new': L('New tenant'),
  'superadmin.tenants.empty': L('No tenants yet.'),
  'superadmin.tenants.colId': L('ID'),
  'superadmin.tenants.colName': L('Name'),
  'superadmin.tenants.colStatus': L('Status'),
  'superadmin.tenants.colCreated': L('Created'),
  'superadmin.tenants.colActions': L('Actions'),
  'superadmin.tenants.view': L('View'),
  // Create tenant form
  'superadmin.tenantNew.title': L('Create tenant'),
  'superadmin.tenantNew.subtitle': L(
    'Provision a new customer organisation and primary admin.'
  ),
  'superadmin.tenantNew.id': L('Tenant ID'),
  'superadmin.tenantNew.name': L('Tenant name'),
  'superadmin.tenantNew.contactEmail': L('Contact email (optional)'),
  'superadmin.tenantNew.primaryAdmin': L('Primary admin'),
  'superadmin.tenantNew.adminEmail': L('Admin email'),
  'superadmin.tenantNew.adminPassword': L('Admin password'),
  'superadmin.tenantNew.adminDisplayName': L('Display name (optional)'),
  'superadmin.tenantNew.submit': L('Create tenant'),
  'superadmin.tenantNew.submitting': L('Creating…'),
  'superadmin.tenantNew.cancel': L('Cancel'),
  // Tenant detail
  'superadmin.tenantDetail.notFound': L('Tenant not found.'),
  'superadmin.tenantDetail.status': L('Status'),
  'superadmin.tenantDetail.contactEmail': L('Contact email'),
  'superadmin.tenantDetail.created': L('Created'),
  'superadmin.tenantDetail.customers': L('Customers'),
  'superadmin.tenantDetail.extinguishers': L('Extinguishers'),
  'superadmin.tenantDetail.visits': L('Visits'),
  'superadmin.tenantDetail.users': L('Users'),
  'superadmin.tenantDetail.noUsers': L('No users.'),
  'superadmin.tenantDetail.userEmail': L('Email'),
  'superadmin.tenantDetail.userRoles': L('Roles'),
  'superadmin.tenantDetail.userStatus': L('Status'),
  'superadmin.tenantDetail.deactivated': L('Deactivated'),
  'superadmin.tenantDetail.active': L('Active'),
  'superadmin.tenantDetail.suspend': L('Suspend'),
  'superadmin.tenantDetail.unsuspend': L('Reactivate'),
  'superadmin.tenantDetail.impersonate': L('Impersonate'),
  // Platform audit
  'superadmin.audit.title': L('Platform audit log'),
  'superadmin.audit.subtitle': L('Every superadmin action.'),
  'superadmin.audit.empty': L('No audit rows.'),
  'superadmin.audit.colAt': L('When'),
  'superadmin.audit.colActor': L('Actor'),
  'superadmin.audit.colAction': L('Action'),
  'superadmin.audit.colTarget': L('Tenant'),
  'superadmin.audit.colPayload': L('Payload'),
  // Impersonation banner
  'superadmin.banner.impersonating': L('Impersonating tenant'),
  'superadmin.banner.exit': L('Exit impersonation'),
} as const;

export type I18nKey = keyof typeof dict;

export function t(key: I18nKey, lang: Lang): string {
  const entry = dict[key];
  if (!entry) return key;
  return (entry as Record<string, string>)[lang] ?? entry.en;
}

export function useT() {
  const lang = useApp(s => s.lang);
  return (key: I18nKey) => t(key, lang);
}
